/**
 * DestinationScheduler — per-destination day planner.
 *
 * Flips in from the destination header card. A compact (lighter-shade) version
 * of the coloured card stays pinned at the top (name + flag/country + dates +
 * a cog to revisit preferences). Below it the body switches between:
 *   - wizard:     the 5-step preferences flow (first run / via the cog)
 *   - generating: spinner while the Cloud Function builds the plan
 *   - schedule:   the generated day-by-day plan
 *
 * Generation is one cached gemini-2.5-flash call. Activity coordinates are
 * resolved via Places once and cached on the schedule doc so we don't re-spend
 * Places quota. The schedule is cached by an input hash (prefs + activity set +
 * dates) — unchanged inputs re-render the saved plan with no call.
 */

import { useState, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, X, Check, Sparkles, Settings, RefreshCw, Pencil, Armchair, BedDouble, Map as MapIcon, Footprints, MapPin, TramFront, Car, Plane, Plus, Trash2, AlertTriangle, ArrowRight, ExternalLink, Undo2, Star, Lock, LockOpen, Link as LinkIcon, Calendar } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { scheduleGapHeadline, type ScheduleGap } from '../utils/itineraryGaps';
import { openDeepLink } from '../utils/deepLinks';
import { doc, setDoc, getDoc, runTransaction, onSnapshot } from 'firebase/firestore';
import { writeDestinationItem } from '../utils/destinationWrites';
import type { SharedItemField } from '../utils/destinationItems';
import { writeSchedule } from '../utils/scheduleWrites';
import { chooseTransitOrDriving } from '../utils/scheduleTravel';
import { ScheduleDraft } from '../utils/scheduleDraft';
import { scheduleActivities } from '../utils/scheduleActivities';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../services/firebase';
import { loadGoogleMaps } from '../services/googleMapsLoader';
import { fetchDrivingDuration } from '../services/drivingDuration';
import { confirmPlaces } from '../services/verifyPlaces';
import { getRecommendations, type Recommendation } from '../services/geminiService';
import {
  DEFAULT_SCHEDULE_PREFS, migrateSchedulePrefs, formatClock, minutesToTime, timeToMinutes,
  bedTimeToSpan, spanToBedTime, wakingHours, WAKE_MIN, WAKE_MAX, BED_SPAN_MAX, STEP_MIN,
} from '../utils/schedulePrefs';
import type { Destination, SchedulePrefs, Transport, TransportPref, TripConfig } from '../types';
import { boundaryTransport, travelEstimates, withTravelEstimates, reserveEstimatedTravel, preserveEstimateStartEdits, stayTransportContext } from '../utils/scheduleTravel';
import { usePlacesWidget } from '../hooks/usePlacesWidget';
import { numberWord } from '../utils/numberWords';
import { calculatedDurationMinutes } from '../utils/tripUtils';
import { resolveTz } from '../utils/airportTimezones';
import { stampJourneyClocks, compareScheduleTime, scheduleMinute, shiftScheduleTime, clockChangeLabel, projectScheduleDay } from '../utils/scheduleTimezones';
import { WizardLoadingScreen } from './WizardLoadingScreen';
import { TAXONOMY, L1_BY_KEY, ITINERARY_L1, normalizeSubcat, suggestL1Key, transportIcon } from '../constants/taxonomy';
import { SubcategoryIcon } from './SubcategoryIcon';
import { CategoryPicker, ERROR_BOX, FIELD } from './UI';
import { clampToDayWindow } from '../utils/scheduleDayWindow';
import { AvatarGroup, By } from './UserAvatar';
import type { Junction } from '../../functions/src/junctionPolicy.mjs';
import { userMessage } from '../utils/userMessage';
import { destTitle } from '../utils/destinationItems';
import { Modal } from './Modal';

interface DestInfo {
  name: string; country: string; flag: string; emoji: string;
  cityBg: string; accent: string;
}
interface AccomInfo { name?: string; address?: string; area?: string; lat?: number; lng?: number }

const DEFAULT_PREFS: SchedulePrefs = DEFAULT_SCHEDULE_PREFS;

// Migrate old `mix` percentage prefs to the `interests` 1–5 scale, then hand
// off to migrateSchedulePrefs for the newer fields (wake/bed/late nights from
// the retired 1–5 `sleep` question, and the transport preference).
/**
 * Convert the ORIGINAL `mix` shape — four raw weights — into the 1-5 interest
 * ratings, then hand the whole thing to migrateSchedulePrefs for the rest.
 *
 * The guard matters: this divided by the total with a `|| 1` fallback and
 * clamped to 1, so prefs saved without a mix at all — `{ pace, sleep }`, the
 * shape stored before the mix existed — came back with EVERY interest at the
 * floor, and the generator built plans for someone apparently uninterested in
 * sightseeing, arts, food and nightlife. Absent is not "not interested".
 */
function migratePrefs(p: any): SchedulePrefs {
  if (p?.interests) return migrateSchedulePrefs(p);
  const mix = p?.mix || {};
  const total = (mix.sightseeing || 0) + (mix.food || 0) + (mix.arts || 0) + (mix.nightlife || 0);
  // Nothing to convert, so nothing to say: let the defaults stand.
  if (total <= 0) return migrateSchedulePrefs(p);
  const toRating = (v: number) => Math.max(1, Math.min(5, Math.round((v / total) * 10)));
  return migrateSchedulePrefs({
    ...p,
    interests: {
      sightseeing: toRating(mix.sightseeing || 0),
      // No equivalent in the old shape, so it takes the middle.
      exploring: 3,
      arts:       toRating(mix.arts || 0),
      food:       toRating(mix.food || 0),
      nightlife:  toRating(mix.nightlife || 0),
    },
  });
}

const INTERESTS = [
  { k: 'sightseeing' as const, label: L1_BY_KEY.experiences.label, desc: 'Hit the most iconic landmarks, monuments and viewpoints' },
  { k: 'exploring'   as const, label: 'Exploring',      desc: 'Wander through neighbourhoods, markets and natural spaces' },
  { k: 'arts'        as const, label: 'Arts & Culture', desc: 'Discover museums, galleries, architecture and local history' },
  { k: 'food'        as const, label: L1_BY_KEY.food.label, desc: 'Sample top-rated restaurants, bars and cafés' },
  { k: 'nightlife'   as const, label: 'Nightlife',      desc: 'Immerse yourself in live music, shows and the late-night scene' },
];
const STEPS = ['Pace', 'Routine', 'Interests', 'Getting around', 'Budget'] as const;
// Hoisted out of each step so the modal can show the question as its heading,
// matching the trip-creation wizard's layout.
const STEP_QUESTIONS = [
  'How packed do you like your days?',
  'How do your days usually run?',
  'What are you most interested in?',
  'How do you like to get around?',
  'What is your overall spending ceiling?',
] as const;

const LATE_NIGHT_CHOICES = [
  ['rarely',    'Rarely',    'Early starts matter more than late finishes'],
  ['sometimes', 'Sometimes', 'The odd late one is fine, then a slower morning'],
  ['often',     'Often',     'Late nights are part of the trip'],
] as const;

const TRANSPORT_CHOICES = [
  ['avoid-public',  'Avoid public transport', 'Plan around taxis, rideshare or driving'],
  ['prefer-public', 'Prefer public transport', 'Use buses, trains and trams where they run'],
  ['fastest',       'Whichever is fastest',    'Pick per leg on travel time alone'],
] as const;

/** A 30-minute-increment time picker. `display` is pre-formatted by the caller
 *  because bed times run past midnight and are held as an offset, not a clock. */
function TimeSlider({ label, display, value, min, max, onChange }: {
  label: string; display: string; value: number; min: number; max: number; onChange: (v: number) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <div className="ds-label">{label}</div>
        <div className="font-serif text-xl font-bold text-app-text tabular-nums">{display}</div>
      </div>
      <input
        type="range" min={min} max={max} step={STEP_MIN} value={value}
        aria-label={label}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full accent-gold"
      />
    </div>
  );
}

// Schedule slot icons reuse the canonical L1 taxonomy icons (single source of
// truth) so the planner shares the itinerary/expense icon language. The slot
// type picker (TYPE_CHOICES) matches the itinerary L1 categories exactly, plus
// Free time and Other. Legacy keys from older schedules / AI output (meal,
// sightseeing, arts, daytrip, travel) are kept as aliases so saved plans still
// render. All icons render in gold.
/**
 * Slots carry a vocabulary of their own: the generation prompt emits
 * 'sightseeing', 'meal', 'arts' and 'daytrip', and years of saved plans hold
 * the same. Translating it to taxonomy L1 keys in ONE place is what stops the
 * icon, the type picker and the label disagreeing about what a slot is —
 * 'arts' and 'daytrip' used to carry a Palette and a Map because they have no
 * L1 of their own, while the taxonomy-keyed picker found no match for them at
 * all and showed a blank type.
 *
 * 'free' and 'travel' are genuinely not categories and pass through.
 */
const SLOT_TYPE_ALIAS: Record<string, string> = {
  sightseeing: 'experiences',
  // Both are Sightseeing: "Arts & Culture" is an L2 under it, and a day trip
  // is how you get to one, not a category of its own.
  arts: 'experiences',
  daytrip: 'experiences',
  meal: 'food',
};
export const normalizeSlotType = (type?: string): string => SLOT_TYPE_ALIAS[type || ''] || type || 'other';

const SLOT_ICON: Record<string, LucideIcon> = {
  transport:     L1_BY_KEY.transport.icon,      // Plane
  accommodation: L1_BY_KEY.accommodation.icon,  // BedDouble
  food:          L1_BY_KEY.food.icon,           // Utensils
  experiences:   L1_BY_KEY.experiences.icon,    // Binoculars (Sightseeing)
  nightlife:     L1_BY_KEY.nightlife.icon,      // Ticket (Entertainment)
  shopping:      L1_BY_KEY.shopping.icon,       // ShoppingBag
  free:          Armchair,                       // Free time (distinct — not Coffee)
  other:         L1_BY_KEY.other.icon,          // Tag
  travel:        L1_BY_KEY.transport.icon,       // a synthesised journey leg
};

// Icon for a synthesised travel-journey slot, by its leg kind.
const TRAVEL_KIND_ICON: Record<string, LucideIcon> = {
  flight: transportIcon('Flight'), train: transportIcon('Train'), bus: transportIcon('Bus'),
  ferry: transportIcon('Boat'), car: transportIcon('Car'), other: transportIcon('Other'),
};


/**
 * Best geocodable query for a place, degrading gracefully: a real street
 * address → the neighbourhood/area → the city. Generic lodging words are
 * stripped from the name (e.g. "Airbnb in Le Marais" → "Le Marais") so a vague
 * name doesn't match a random same-named business; if nothing usable remains we
 * fall back to the city rather than mis-anchoring.
 */
function bestLocationQuery(p: { name?: string; address?: string; area?: string }, city: string): string {
  if (p.address && /\d/.test(p.address)) return p.address;          // has a street number → trust it
  if (p.address) return `${p.address}, ${city}`;
  if (p.area) return `${p.area}, ${city}`;
  const hint = (p.name || '').replace(/\bairbnb\b|\bapartment\b|\bapt\b|\bflat\b|\bstudio\b|\brental\b|\bin\b|\bat\b|[-–—]/gi, ' ').replace(/\s+/g, ' ').trim();
  return hint ? `${hint}, ${city}` : city;
}

function hashInputs(prefs: SchedulePrefs, actIds: string[], dates: string[]): string {
  const s = JSON.stringify({ p: prefs, a: [...actIds].sort(), d: dates });
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return String(h);
}

/** 1–5 segmented scale with end labels. */
function Scale5({ value, onChange, left, right }: { value: number; onChange: (v: number) => void; left?: string; right?: string }) {
  return (
    <div className="space-y-2.5">
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map(n => (
          <button key={n} onClick={() => onChange(n)}
            className={`flex-1 h-11 rounded-2xl border-2 transition-all font-black text-sm ${value === n ? 'bg-gold border-gold text-white shadow-md shadow-gold/20 scale-105' : 'bg-app-faint border-app-border/40 text-app-sub/50 hover:border-gold/40'}`}>
            {n}
          </button>
        ))}
      </div>
      {(left || right) && (
        <div className="flex justify-between ds-label px-1">
          <span>{left}</span><span>{right}</span>
        </div>
      )}
    </div>
  );
}

/** Turn a Firebase callable error into a sentence the traveller can act on. */
function scheduleErrorMsg(e: any): string {
  const code: string = e?.code ?? '';
  const raw: string  = e?.message ?? '';
  if (/deadline.exceeded|timeout/i.test(code + raw))
    return 'We are having some trouble generating your plan. Please try again in a few minutes.';
  if (/resource.exhausted|quota|429|credits/i.test(code + raw))
    return 'AI suggestions are temporarily unavailable (quota reached). Please try again in a moment.';
  if (/unauthenticated/i.test(code))
    return 'You need to be signed in to generate a plan.';
  if (/unavailable|network/i.test(code + raw))
    return 'Couldn\'t reach the server — check your connection and try again.';
  if (!raw || /^internal$/i.test(raw.trim()))
    return 'Something went wrong generating your plan. Please try again.';
  return raw;
}

export function DestinationScheduler({ dest, dates, itinerary, activeDate, dayBadge, headerTints, otherDestinations, tripId, destId, currentUserId, travellers = [], junctions = [], accommodation, accomNightsMissing = 0, prevDest, sharedDay, travelContext, onClose, onExitToItinerary }: {
  /** Nights of this stay no booking covers — makes accommodation read as partly missing. */
  accomNightsMissing?: number;
  dest: DestInfo;
  dates: { date: string; label: string; dow: string; shared?: boolean }[];
  itinerary: TripConfig['itinerary'];
  /** "Day X of Y" within this destination's own stay — shown above the day's
   *  agenda title. Omitted on transit days (the split strip signals those). */
  dayBadge?: string;
  /** One accent hex per DestinationStrip side (1 normally, 2 on a transit
   *  day) — washes the day-header block in the same colour(s) the strip
   *  above it is showing, split left/right on a transit day to match. */
  headerTints: string[];
  /** Every OTHER destination's name on this trip — passed straight through
   *  to the generator so it never suggests a day-trip to somewhere that's
   *  already a separate stop elsewhere in the itinerary. */
  otherDestinations: string[];
  /** Which day's schedule to show — controlled by the parent (the destination
   *  hero card and its date strip now live there too, shared with Shortlists
   *  mode), not owned internally here. */
  activeDate: string;
  tripId: string;
  destId: string;
  currentUserId: string;
  /** Names for subset bookings ("Ann only"); a shared plan is not rewritten around them. */
  travellers?: { id: string; name: string }[];
  /** Safe split/rejoin events anchored at this stop (R8): a rough time, never a slot. */
  junctions?: Junction[];
  accommodation?: AccomInfo;
  /** The destination visited immediately before this one — used to ground the
   *  travel-day journey (geocodable departure accommodation + same/diff country
   *  for the pre-departure buffer). `destId` is its schedule doc id (its arrival
   *  date), used to avoid duplicating its already-planned activities. */
  prevDest?: { name?: string; country?: string; accomName?: string; accomAddress?: string; destId?: string };
  /** Present only on a destination that has a NEXT destination: the departure
   *  day (= the next destination's arrival date) is shown in THIS date bar but
   *  is owned by the arriving destination's schedule doc (`ownerDestId`). Edits
   *  to it round-trip to that doc, so the travel day stays in sync across both. */
  sharedDay?: { date: string; ownerDestId: string };
  travelContext: { visit: Destination; previous?: Destination; next?: Destination; transports: Transport[] };
  /** Omit when the scheduler is a standalone view with nothing to "close" back
   *  to (e.g. the Day Planner tab) — the toolbar's Close button only renders
   *  when this is provided. */
  onClose?: (activityId?: string) => void;
  /** "View saved item" on a linked activity — a separate concern from
   *  onClose (viewing something elsewhere isn't the same as closing this
   *  view), so it works even when there's no Close button. */
  onExitToItinerary?: (activityId: string) => void;
}) {
  const [mode, setMode] = useState<'loading' | 'load-error' | 'wizard' | 'generating' | 'schedule'>('loading');
  const [step, setStep] = useState(0);
  const [hasPrefs, setHasPrefs] = useState(false);
  // The wizard opens either from the cog (tweak prefs) or as the gateway to the
  // first auto-fill — pendingFill remembers which, so Save runs the right action.
  const [pendingFill, setPendingFill] = useState(false);
  const [fillingDay, setFillingDay] = useState<number | null>(null);
  const [prefs, setPrefs] = useState<SchedulePrefs>(DEFAULT_PREFS);
  const [schedule, setSchedule] = useState<any | null>(null);
  const [roadMinutes, setRoadMinutes] = useState<Record<string, number | null>>({});
  // Schedules created before `source: 'ai'` was stored still carry inputHash.
  const [legacyGenerated, setLegacyGenerated] = useState(false);
  const roadLegs = (['arrival', 'departure'] as const).flatMap(direction => {
    const leg = boundaryTransport(travelContext.visit, travelContext.transports, direction);
    return leg && /^(car|taxi)$/i.test(leg.mode)
      && !/T\d{2}:\d{2}/.test(direction === 'arrival' ? leg.arr : leg.dep)
      && (!leg.duration || /^tbc$/i.test(leg.duration.trim())) ? [leg] : [];
  });
  const roadRoutes = roadLegs.map(leg => [leg.fromAddress || leg.from, leg.toAddress || leg.to] as const)
    .filter(([from, to]) => !!from && !!to && from.toLowerCase() !== to.toLowerCase());
  const roadRoutesKey = JSON.stringify(roadRoutes);
  useEffect(() => {
    let active = true;
    roadRoutes.forEach(([from, to]) => fetchDrivingDuration(from, to).then(minutes => {
      if (active) setRoadMinutes(current => ({ ...current, [JSON.stringify([from, to])]: minutes }));
    }));
    return () => { active = false; };
  }, [roadRoutesKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const routedMinutes = (leg: Transport) => roadMinutes[JSON.stringify([leg.fromAddress || leg.from, leg.toAddress || leg.to])] ?? null;
  const estimates = travelEstimates(travelContext.visit, travelContext.previous, travelContext.next, travelContext.transports, routedMinutes)
    .filter(e => e.date !== sharedDay?.date && dates.some(d => d.date === e.date));
  const sharedEstimates = sharedDay && travelContext.next
    ? travelEstimates(travelContext.next, travelContext.visit, undefined, travelContext.transports, routedMinutes)
      .filter(e => e.date === sharedDay.date && e.slot.id.startsWith('estimated-arrival-')) : [];
  const currentDays = schedule?.days || [];
  const viewSchedule = withTravelEstimates({ ...schedule, days: [...currentDays,
    ...dates.filter(d => !currentDays.some((day: any) => day.date === d.date)).map(d => ({ date: d.date, title: '', slots: [] })),
  ] }, [...estimates, ...sharedEstimates]);
  // The arriving destination's schedule that owns the shared travel day. Loaded
  // live for display + leg coords. Only set when `sharedDay` is present.
  const [sharedOwner, setSharedOwner] = useState<any | null>(null);
  const [coordCache, setCoordCache] = useState<Record<string, { lat: number; lng: number }>>({});
  // App's itinerary subscription owns commitment changes. Geocoding may enrich
  // coordinates, but must never replace newer item fields with a stale snapshot.
  // Safe junction facts for generation: date, rough time and a head count only.
  const groupChangesFor = (days: string[]) => junctions.filter(e => days.includes(e.date))
    .map(e => ({ date: e.date, kind: e.kind, ...(e.time ? { time: e.time } : {}), travellers: e.participantIds.length }));
  const availableActs = scheduleActivities(itinerary, dates).map(a =>
    a.lat == null && coordCache[a.id] ? { ...a, ...coordCache[a.id] } : a);
  const [error, setError] = useState<string | null>(null);
  const [loadVersion, setLoadVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const scheduleDraft = useRef(new ScheduleDraft({ days: [] }));
  const saveFailed = useRef(false);
  const [failedSave, setFailedSave] = useState(false);
  const [liveError, setLiveError] = useState(false);
  const [liveVersion, setLiveVersion] = useState(0);

  // Insert the arriving-destination-owned travel day (from `owner`) into THIS
  // destination's schedule for display. The shared day is never stored in this
  // destination's own doc — saveSchedule routes it back to the owner.
  const withSharedDay = (sched: any, owner: any) => {
    if (!sharedDay || !sched) return sched;
    const ownerDay = (owner?.days || []).find((d: any) => d.date === sharedDay.date)
      || { date: sharedDay.date, title: '', slots: [] as any[] };
    const without = (sched.days || []).filter((d: any) => d.date !== sharedDay.date);
    return { ...sched, days: [...without, ownerDay].sort((a: any, b: any) => (a.date || '').localeCompare(b.date || '')) };
  };
  // This destination's OWN dates (the shared travel day belongs to the owner).
  const ownDates = sharedDay ? dates.filter(d => d.date !== sharedDay.date) : dates;
  const blankOwn = () => ({ days: ownDates.map(d => ({ date: d.date, title: '', slots: [] as any[] })) });

  // Load any saved prefs + schedule on mount.
  useEffect(() => {
    let cancelled = false;
    setMode('loading');
    (async () => {
      // Fetch the arriving destination's schedule that owns the shared travel
      // day. A failed read must never become an editable blank schedule.
      let owner: any = null;
      try {
        // Independent documents must not cost two sequential network round trips.
        const [snap, os] = await Promise.all([
          getDoc(doc(db, 'trips', tripId, 'schedules', destId)),
          sharedDay ? getDoc(doc(db, 'trips', tripId, 'schedules', sharedDay.ownerDestId)) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        if (os?.exists()) {
            owner = os.data().schedule || null;
            setSharedOwner(owner);
            if (os.data().coords) setCoordCache(c => ({ ...os.data().coords, ...c }));
        }
        const data = snap.exists() ? snap.data() : null;
        setLegacyGenerated(!!data?.inputHash);
        if (data?.prefs) { setPrefs(migratePrefs(data.prefs)); setHasPrefs(true); }
        else {
          // Reuse this trip's previously-set preferences (from an earlier
          // destination's wizard) so the traveller isn't asked twice.
          try {
            const tp = await getDoc(doc(db, 'trips', tripId, 'schedules', '_prefs'));
            if (tp.exists() && tp.data()?.prefs) { setPrefs(migratePrefs(tp.data()!.prefs)); setHasPrefs(true); }
          } catch { /* non-fatal */ }
        }
        if (data?.coords) setCoordCache(c => ({ ...c, ...data.coords }));
        if (data?.schedule?.days?.length) {
          // Prune for display; persist this cleanup only with a conflict-checked edit.
          const pruned = pruneTravelSlots(data.schedule);
          scheduleDraft.current = new ScheduleDraft(withSharedDay(data.schedule, owner));
          scheduleDraft.current.current = withSharedDay(pruned, owner);
          setSchedule(withSharedDay(pruned, owner));
          setMode('schedule');
        } else {
          // No saved schedule — open straight onto an editable blank canvas (no
          // up-front AI-vs-manual fork). Nothing is persisted until first edit.
          const blank = withSharedDay(blankOwn(), owner);
          scheduleDraft.current = new ScheduleDraft(blank);
          setSchedule(blank);
          setMode('schedule');
        }
        // React state setters above have not updated this effect's closure yet.
        // Pass the loaded cache directly instead of geocoding saved places again.
        prep({ ...os?.data()?.coords, ...data?.coords }).catch(() => {});
      } catch {
        if (cancelled) return;
        setError('Could not load the saved schedule. Check your connection and try again. Nothing has been overwritten.');
        setMode('load-error');
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadVersion]);

  // Reattach after a save to receive snapshots skipped while the draft was dirty.
  // Generation keeps its original baseline until its result has been saved too.
  const liveReady = mode === 'schedule' || mode === 'wizard';
  useEffect(() => {
    if (!liveReady || saving || failedSave || fillingDay !== null) return;
    let stopped = false;
    let own: any;
    let owner: any = sharedDay ? undefined : null;
    const receive = () => {
      if (stopped || own === undefined || owner === undefined || savingRef.current || saveFailed.current) return;
      const latest = withSharedDay(own?.schedule ?? blankOwn(), owner?.schedule);
      if (!scheduleDraft.current.receive(latest)) return;
      scheduleDraft.current.current = pruneTravelSlots(latest);
      setSchedule(scheduleDraft.current.current);
      setLegacyGenerated(!!own?.inputHash);
      setSharedOwner(owner?.schedule ?? null);
      setCoordCache(cache => ({ ...cache, ...owner?.coords, ...own?.coords }));
      setLiveError(false);
    };
    const failed = () => { if (!stopped) { stopped = true; setLiveError(true); } };
    const listen = (id: string, isOwner: boolean) => onSnapshot(
      doc(db, 'trips', tripId, 'schedules', id), { includeMetadataChanges: true },
      snapshot => {
        if (stopped || snapshot.metadata.fromCache || snapshot.metadata.hasPendingWrites) return;
        if (isOwner) owner = snapshot.data() ?? null;
        else own = snapshot.data() ?? null;
        receive();
      }, failed,
    );
    const stopOwn = listen(destId, false);
    const stopOwner = sharedDay ? listen(sharedDay.ownerDestId, true) : undefined;
    return () => { stopped = true; stopOwn(); stopOwner?.(); };
  // Values such as dates/withSharedDay are derived for the mounted destination.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveReady, saving, failedSave, fillingDay, liveVersion, loadVersion, tripId, destId, sharedDay?.date, sharedDay?.ownerDestId]);

  const setInterest = (k: keyof SchedulePrefs['interests'], v: number) =>
    setPrefs(p => ({ ...p, interests: { ...p.interests, [k]: v } }));

  // Gather the destination's activities from its itinerary day docs (deduped —
  // items repeat across a destination's days).
  async function gatherActivities() {
    const hhmm = (s?: string) => { const m = s && /(?:T|^)(\d{2}:\d{2})/.exec(s); return m ? m[1] : undefined; };
    const records = await Promise.all(dates.map(async ({ date }) => {
      const snap = await getDoc(doc(db, 'trips', tripId, 'itinerary', date));
      return [date, snap.data()] as const;
    }));
    const acts = scheduleActivities(Object.fromEntries(records), dates);

    // Arrival: latest-arriving transport on day 1. Departure: earliest-departing
    // transport on the checkout day (the day after the last staying date).
    let arrival: ArrivalInfo | undefined;
    let departure: { date: string; time?: string; assumed?: boolean } | undefined;
    try {
      // The latest-arriving transport tells us where (airport/station) and, if
      // supplied, when the traveller lands. Derive a geocodable arrival location; for a
      // car/taxi leg there's no terminal, so assume they head to the lodging.
      const arrTr = boundaryTransport(travelContext.visit, travelContext.transports, 'arrival');
      if (arrTr) {
        const isCar = /\b(car|drive|driving|taxi|rideshare|uber)\b/i.test(`${arrTr.mode || ''} ${arrTr.icon || ''}`);
        const accomLoc = accommodation?.address || accommodation?.name;
        const terminalLoc = arrTr.toAddress
          || (arrTr.to ? `${arrTr.to}${arrTr.toCity ? `, ${arrTr.toCity}` : `, ${dest.name}`}` : undefined)
          || (arrTr.toCode ? `${arrTr.toCode}, ${dest.name}` : undefined);
        const location = isCar ? (accomLoc || terminalLoc) : (terminalLoc || accomLoc);
        // International vs domestic (for the pre-departure buffer) from the
        // previous destination's country; previous accommodation is the
        // geocodable origin for the leg to the departure port.
        const sameCountry = prevDest?.country && dest.country
          ? prevDest.country.trim().toLowerCase() === dest.country.trim().toLowerCase()
          : undefined;
        arrival = {
          date: dates[0].date, time: hhmm(arrTr.arr) || ASSUMED_ARRIVAL, assumed: !hhmm(arrTr.arr), location,
          originCity: prevDest?.name || arrTr.fromCity,
          transportId: arrTr.id || arrTr.flightNum || String(arrTr.dep),
          dep: arrTr.dep?.slice(0, 10) === dates[0].date ? hhmm(arrTr.dep) : undefined, arr: hhmm(arrTr.arr),
          mode: arrTr.mode, carrier: arrTr.carrier,
          clockTransport: { dep: arrTr.dep, arr: arrTr.arr,
            depTz: resolveTz(arrTr.depTz, arrTr.fromCode, arrTr.from, arrTr.fromCity),
            arrTz: resolveTz(arrTr.arrTz, arrTr.toCode, arrTr.to, arrTr.toCity),
            fromCode: arrTr.fromCode, toCode: arrTr.toCode, from: arrTr.from, to: arrTr.to,
            fromCity: arrTr.fromCity, toCity: arrTr.toCity },
          durationMin: calculatedDurationMinutes(arrTr, arrTr.dep, arrTr.arr, arrTr.depTz, arrTr.arrTz),
          fromPort: arrTr.from, fromAddress: arrTr.fromAddress,
          toPort: arrTr.to, toCity: arrTr.toCity,
          accomLoc, accomName: accommodation?.name,
          sameCountry,
          prevAccomLoc: prevDest?.accomAddress || prevDest?.accomName,
          prevAccomName: prevDest?.accomName,
        };
      }

      const depTr = boundaryTransport(travelContext.visit, travelContext.transports, 'departure');
      const depTime = hhmm(depTr?.dep);
      departure = { date: travelContext.visit.departureDate, time: depTime || ASSUMED_DEPARTURE, assumed: !depTime };
    } catch { /* non-fatal */ }

    // Nothing booked into this destination — no transport leg, and possibly no
    // previous destination either. Anchor the day anyway: see ASSUMED_ARRIVAL.
    // Accommodation is assumed to exist even when it hasn't been entered; the
    // check-in placeholder carries no location until one is, at which point the
    // transfer legs either side of it start being drawn like any other.
    if (!arrival) {
      const accomLoc = accommodation?.address || accommodation?.name;
      arrival = {
        date: dates[0].date, time: ASSUMED_ARRIVAL, arr: ASSUMED_ARRIVAL, assumed: true,
        location: accomLoc, accomLoc, accomName: accommodation?.name,
        originCity: prevDest?.name,
      };
    }

    return { acts, arrival, departure };
  }

  // Resolve missing coordinates via Places, using the cache first.
  async function resolveCoords(acts: any[], accom: AccomInfo | undefined, savedCoords = coordCache) {
    const cache = { ...savedCoords };
    const needs = acts.filter(a => a.lat == null && cache[a.id] == null && (a.address || a.name));
    const accomNeeds = accom && accom.lat == null && !cache['__accom'] && (accom.address || accom.name);
    if (needs.length || accomNeeds) {
      await loadGoogleMaps();
      const g = (window as any).google;
      if (g?.maps?.places) {
        const svc = new g.maps.places.PlacesService(document.createElement('div'));
        const find = (q: string) => new Promise<{ lat: number; lng: number } | null>(res => {
          svc.findPlaceFromQuery({ query: q, fields: ['geometry'] }, (r: any, s: any) => {
            const loc = s === g.maps.places.PlacesServiceStatus.OK && r?.[0]?.geometry?.location;
            res(loc ? { lat: loc.lat(), lng: loc.lng() } : null);
          });
        });
        for (const a of needs) {
          const c = await find(`${a.name}, ${a.address || dest.name}`);
          if (c) cache[a.id] = c;
        }
        if (accomNeeds) {
          const c = await find(bestLocationQuery(accom!, dest.name));
          if (c) cache['__accom'] = c;
        }
      }
    }
    // merge cached coords onto activities
    acts.forEach(a => {
      if (a.lat == null && cache[a.id]) { a.lat = cache[a.id].lat; a.lng = cache[a.id].lng; }
      // Seed the cache with pre-existing coords too, so every mappable activity
      // is resolvable by id when building schedule map routes.
      else if (a.lat != null && a.lng != null && !cache[a.id]) cache[a.id] = { lat: a.lat, lng: a.lng };
    });
    const accomOut = accom ? { ...accom, lat: accom.lat ?? cache['__accom']?.lat, lng: accom.lng ?? cache['__accom']?.lng } : undefined;
    return { acts, accom: accomOut, cache };
  }

  // Gather + geocode the destination's activities, caching coords. Used by both
  // AI generation and the manual builder (its add-spot picker + map routes).
  async function prep(savedCoords = coordCache) {
    const { acts: raw, arrival, departure } = await gatherActivities();
    const { acts, accom, cache } = await resolveCoords(raw, accommodation, savedCoords);
    setCoordCache(current => ({ ...current, ...cache }));
    return { acts, arrival, departure, accom, cache };
  }

  // Deselect a committed-but-not-yet-scheduled item from the Pending Bucket
  // List review modal — same underlying field (inPlan) the "Commit to Plan"
  // toggle uses, just flipped from here and written straight to its day doc.
  const ARR_KEY_FOR_TYPE: Record<string, SharedItemField> = { attraction: 'attractions', eat: 'eats', nightlife: 'nightlife', shopping: 'shopping' };
  async function uncommitPending(item: any) {
    const arrKey = ARR_KEY_FOR_TYPE[item.type];
    if (!arrKey || !item.dayDate) return;
    try {
      await runTransaction(db, transaction => writeDestinationItem(transaction, db, tripId, item.dayDate, arrKey, item.id, { inPlan: false }));
    } catch {
      setError('Could not remove this item from the schedule queue. Check your connection and try again.');
    }
  }

  const scheduleDoc = () => doc(db, 'trips', tripId, 'schedules', destId);
  const saveTripPrefs = (p: SchedulePrefs) =>
    setDoc(doc(db, 'trips', tripId, 'schedules', '_prefs'), { prefs: p, updatedAt: new Date().toISOString(), updatedBy: currentUserId }, { merge: true }).catch(() => {});

  async function generate() {
    setError(null);
    setMode('generating');
    try {
      const { acts, arrival, departure, accom, cache } = await prep();
      // The route has already assigned travel-day ownership (including private
      // changeovers). A late checkout must not add another scope's day back.
      const cfDates = ownDates.map(d => d.date);
      // When we plan an origin-morning before the journey, fetch the previous
      // destination's already-planned spots so the model doesn't repeat them.
      let originPlanned: string[] | undefined;
      if (arrival?.originCity && prevDest?.destId) {
        try {
          const ps = await getDoc(doc(db, 'trips', tripId, 'schedules', prevDest.destId));
          const pdays: any[] = ps.exists() ? (ps.data().schedule?.days || []) : [];
          originPlanned = Array.from(new Set(
            pdays.flatMap(d => (d.slots || []).filter((s: any) => s.type !== 'travel' && s.title).map((s: any) => s.title as string))
          )).slice(0, 40);
        } catch { /* non-fatal */ }
      }
      // Locked, non-journey slots (booked imports, or anything the traveller
      // manually pinned) must survive a full regenerate untouched — journey
      // slots are also `locked` but are already fully handled via the
      // `arrival`/`departure`/estimates machinery below, so they're excluded
      // here to avoid double-handling them.
      const lockedByDate = new Map<string, any[]>();
      (schedule?.days || []).forEach((d: any) => {
        if (!cfDates.includes(d.date)) return;
        const locked = projectScheduleDay(d, travelContext.transports).slots.filter((s: any) => s.locked && !s.journey);
        if (locked.length) lockedByDate.set(d.date, locked);
      });
      // Match the function's 300s timeout (the client default is 70s, which made
      // slow-but-successful generations falsely surface as a timeout error).
      const fn = httpsCallable(functions, 'generateSchedule', { timeout: 300000 });
      const res: any = await fn({
        destination: dest.name, country: dest.country,
        dates: cfDates,
        accommodation: accom ? { name: accom.name, lat: accom.lat, lng: accom.lng } : undefined,
        // Transport items are handled via the journey context — never sent as
        // must-include activities (else they get placed as raw, unstyled slots).
        activities: acts.filter(a => a.type !== 'transport').map(a => ({ id: a.id, name: a.name, category: a.category, type: a.type, inPlan: a.inPlan, lat: a.lat, lng: a.lng })),
        prefs,
        otherDestinations,
        groupChanges: groupChangesFor(cfDates),
        stayTransports: stayTransportContext(travelContext.visit, travelContext.transports),
        arrival: arrival ? {
          ...arrival,
          // Earlier-day departures must not become a new departure on arrival day.
          transportId: arrival.dep ? arrival.transportId : undefined,
          originCity: arrival.dep ? arrival.originCity : undefined,
          originAccom: arrival.prevAccomName || arrival.prevAccomLoc,
          destAccom: arrival.accomName || arrival.accomLoc,
          ...(originPlanned?.length ? { originPlanned } : {}),
        } : undefined,
        departure: departure && cfDates.includes(departure.date) ? departure : undefined,
        existing: [
          ...estimates.map(e => ({ date: e.date, slots: [e.slot] })),
          ...Array.from(lockedByDate, ([date, slots]) => ({ date, slots: slots.map((s: any) =>
            ({ start: s.start, durationMin: s.durationMin, type: s.type, title: s.title })) })),
        ],
      });
      const generated0 = postProcessGenerated({ ...res.data, days: (res.data?.days || []).filter((d: any) => cfDates.includes(d.date)) }, dest.name, arrival,
        departure && cfDates.includes(departure.date) ? departure : undefined, { wakeTime: prefs.wakeTime, bedTime: prefs.bedTime });
      const mustInclude = acts.filter(a => a.type !== 'transport' && a.inPlan).map(a => ({ id: a.id, name: a.name, type: a.type }));
      const generated = ensureMustIncludeSlots(generated0, mustInclude);
      generated.days = generated.days.map((day: any) => ({ ...day,
        slots: reserveEstimatedTravel(day.slots || [], estimates.filter(e => e.date === day.date).map(e => e.slot)),
      }));
      generated.days = reserveLockedSlots(generated.days, lockedByDate);
      // Regenerating a clipped personal visit must leave the other travellers'
      // dates (and this traveller's later return to the same stop) untouched.
      generated.days = [...(schedule?.days || []).filter((d: any) => !cfDates.includes(d.date)), ...generated.days]
        .sort((a: any, b: any) => a.date.localeCompare(b.date));
      const inputHash = hashInputs(prefs, acts.map(a => a.id), ownDates.map(d => d.date));
      await saveSchedule(generated, { prefs, inputHash, coords: cache });
    } catch (e: any) {
      console.error('generateSchedule failed', e);
      setError(scheduleErrorMsg(e));
      setMode(schedule ? 'schedule' : 'wizard');
    }
  }

  const saveAndGenerate = async () => {
    try {
      await setDoc(scheduleDoc(), { prefs, updatedAt: new Date().toISOString(), updatedBy: currentUserId }, { merge: true });
      setPendingFill(false);
      setHasPrefs(true);
      saveTripPrefs(prefs);
      generate();
    } catch { setError('Could not save your preferences. Check your connection and try again.'); }
  };

  // Save edited preferences without regenerating — prefs feed future fills.
  const savePrefsOnly = async () => {
    try {
      await setDoc(scheduleDoc(), { prefs, updatedAt: new Date().toISOString(), updatedBy: currentUserId }, { merge: true });
      setHasPrefs(true);
      saveTripPrefs(prefs);
      setMode('schedule');
    } catch { setError('Could not save your preferences. Check your connection and try again.'); }
  };

  // Whole-trip auto-fill gateway: ask preferences once, then generate; on later
  // runs go straight to generation.
  const autoFillTrip = () => {
    if (hasPrefs) generate();
    else { setStep(0); setPendingFill(true); setMode('wizard'); }
  };

  // Persist a schedule, routing the shared travel day to its owning destination
  // (single source of truth) and this destination's own days to its own doc.
  // `extra` carries side fields (coords/prefs/inputHash) onto THIS doc only.
  const saveSchedule = async (next: any, extra: Record<string, any> = {}) => {
    // Derived allowances are display-only, except a traveller's chosen start
    // time, which must survive reloads while the itinerary time is still unknown.
    next = { ...next, days: (next.days || []).map((day: any) => ({ ...day,
      slots: (day.slots || []).filter((slot: any) => !slot.estimatedTravel || slot.startOverride),
    })) };
    // Generation covers own days only; absence must not delete the owner's day.
    const completeNext = sharedDay && !next.days.some((day: any) => day.date === sharedDay.date)
      ? withSharedDay(next, { days: scheduleDraft.current.current.days }) : next;
    // `schedule` is the render snapshot that produced this edit, even for delayed callbacks.
    scheduleDraft.current.edit(schedule ?? scheduleDraft.current.current, completeNext, extra);
    setSchedule(scheduleDraft.current.current);
    setMode('schedule');
    if (!saveFailed.current) await flushSchedule();
  };

  const flushSchedule = async () => {
    if (savingRef.current) return; // The running drain will also persist newly queued edits.
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      await scheduleDraft.current.flush(async (next, previous, extra) => {
        const saved = await runTransaction(db, transaction => writeSchedule(transaction, db, {
          tripId, destId, userId: currentUserId, previous, next, sharedDay, extra,
        }));
        setSharedOwner(saved.owner);
        return withSharedDay(saved.schedule, saved.owner);
      });
      setSchedule(pruneTravelSlots(scheduleDraft.current.current));
      saveFailed.current = false;
      setFailedSave(false);
    } catch (e) {
      saveFailed.current = true;
      setFailedSave(true);
      setError(userMessage(e, 'Check your connection and try again.'));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // Persist a manual edit to the schedule.
  const persistSchedule = async (next: any) => {
    await saveSchedule(preserveEstimateStartEdits(next, viewSchedule));
  };

  // What the itinerary is still missing for this visit. Cheap enough to
  // recompute per render; boundaryTransport is a
  // pure scan over the trip's transports.
  const missingBoundary = (() => {
    try {
      return {
        arrival: !boundaryTransport(travelContext.visit, travelContext.transports, 'arrival'),
        departure: !boundaryTransport(travelContext.visit, travelContext.transports, 'departure'),
      };
    } catch { return { arrival: false, departure: false }; }
  })();
  const missingAccom = !(accommodation?.address || accommodation?.name);
  // For the "what's missing" banner: nothing at all vs only part of it.
  const logisticsGaps = {
    transport: (missingBoundary.arrival && missingBoundary.departure ? 'none' : missingBoundary.arrival || missingBoundary.departure ? 'some' : null) as ScheduleGap,
    accommodation: (!accommodation ? 'none' : accomNightsMissing > 0 ? 'some' : null) as ScheduleGap,
  };

  // Short free-text preference hint for the Ideas suggestions request.
  const suggestPrefs = (['budget-friendly', 'mid-range', 'premium'][prefs.budget - 1] || '') + ' options';

  // Per-day assistive fill: the day's existing slots stay verbatim and Talisman
  // fills the gaps around them. Runs inline (fillingDay spinner on that day's
  // buttons) so the rest of the schedule stays visible and editable throughout.
  async function fillDay(di: number) {
    if (!viewSchedule?.days?.[di] || fillingDay != null) return;
    const day = viewSchedule.days[di];
    // The shared travel day is generated/owned by the arriving destination — its
    // AI fill happens there, not here. (Manual edits still round-trip.)
    if (sharedDay && day.date === sharedDay.date) return;
    setError(null);
    setFillingDay(di);
    try {
      const { acts, arrival, departure, accom, cache } = await prep();
      const placed = new Set<string>();
      (schedule.days || []).forEach((d: any) => (d.slots || []).forEach((s: any) => { if (s.activityId) placed.add(s.activityId); }));
      // Demote everything to shortlist for a single-day fill — otherwise the
      // model tries to cram every must-include activity into this one day.
      const sendActs = acts.filter(a => !placed.has(a.id) && a.type !== 'transport')
        .map(a => ({ id: a.id, name: a.name, category: a.category, type: a.type, inPlan: false, lat: a.lat, lng: a.lng }));
      const existing = [{
        date: day.date,
        slots: (day.slots || []).map((s: any) => ({ start: s.start, durationMin: s.durationMin, type: s.type, title: s.title })),
      }];
      // Match the function's 300s timeout (the client default is 70s, which made
      // slow-but-successful generations falsely surface as a timeout error).
      const fn = httpsCallable(functions, 'generateSchedule', { timeout: 300000 });
      const res: any = await fn({
        destination: dest.name, country: dest.country,
        dates: [day.date],
        accommodation: accom ? { name: accom.name, lat: accom.lat, lng: accom.lng } : undefined,
        activities: sendActs,
        prefs,
        otherDestinations,
        groupChanges: groupChangesFor([day.date]),
        stayTransports: stayTransportContext(travelContext.visit, travelContext.transports),
        arrival: arrival?.date === day.date ? { ...arrival,
          transportId: arrival.dep ? arrival.transportId : undefined,
          originCity: arrival.dep ? arrival.originCity : undefined,
        } : undefined,
        departure: departure?.date === day.date ? departure : undefined,
        existing,
      });
      const aiDay = (res.data?.days || []).find((x: any) => x.date === day.date) ?? res.data?.days?.[0];
      // Drop travel slots and any re-emitted journey items (kind-tagged) — the
      // day-fill only adds activities around the existing fixed/journey slots.
      const newSlots = reserveEstimatedTravel((aiDay?.slots || []).filter((s: any) => s.type !== 'travel' && !s.kind).map((s: any) => ({ id: uid(), ...s })),
        estimates.filter(e => e.date === day.date).map(e => e.slot));
      const mergedSlots = projectScheduleDay({ ...day, slots: [...(day.slots || []), ...newSlots] }, travelContext.transports).slots;
      const next = { ...schedule, source: 'ai', days: viewSchedule.days.map((d: any, i: number) => i !== di ? d : { ...d, title: d.title || aiDay?.title || '', slots: mergedSlots }) };
      await saveSchedule(next, { coords: cache, prefs });
    } catch (e: any) {
      console.error('fillDay failed', e);
      setError(scheduleErrorMsg(e));
    } finally {
      setFillingDay(null);
    }
  }

  const handleClose = (activityId?: string) => onClose?.(activityId);

  return (
    <div className="space-y-4">
      {/* Only a Close affordance lives up here now. The preferences cog used
          to float alone in this row — an unlabelled icon with no
          neighbours, detached from "Refresh Plan" sitting at the very
          bottom of the page. Both now live together under the day title in
          ScheduleView. */}
      {onClose && (
        <div className="flex justify-end items-center gap-1.5">
          <button aria-label="Close" onClick={() => handleClose()} title="Close"
            className="w-8 h-8 rounded-full bg-white border border-app-border/40 flex items-center justify-center text-app-sub/60 hover:text-rose-500 hover:border-rose-300 transition-colors shadow-sm">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {error && (
        <div role="alert" className={`${ERROR_BOX} mt-4`}>
          {failedSave && <p className="font-semibold mb-1">These schedule changes are not saved.</p>}
          <p>{error}</p>
          {(failedSave || mode === 'load-error') && <div className="mt-2 flex flex-wrap gap-3">
            {failedSave && <button disabled={saving} className="min-h-11 font-semibold underline" onClick={flushSchedule}>Retry save</button>}
            <button disabled={saving} className="min-h-11 font-semibold underline" onClick={() => {
              saveFailed.current = false; setFailedSave(false); setError(null); setLoadVersion(version => version + 1);
            }}>{failedSave ? 'Discard this edit and reload' : 'Retry loading'}</button>
          </div>}
        </div>
      )}
      {saving && <p role="status" className="text-sm text-app-sub">Saving schedule…</p>}
      {liveError && <div role="alert" className="text-sm text-app-sub">
        Live updates are unavailable. Your current schedule is still here.
        <button className="ml-2 min-h-11 font-semibold underline" onClick={() => setLiveVersion(version => version + 1)}>Reconnect</button>
      </div>}

      {mode === 'loading' && (
        <div className="mt-4 flex justify-center py-12"><div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>
      )}

      {mode === 'generating' && (
        <WizardLoadingScreen
          title={`Styling your plans for ${destTitle(dest.name)}…`}
          detail={`Sequencing ${numberWord(ownDates.length)} ${ownDates.length === 1 ? 'day' : 'days'} around your preferences.`}
        />
      )}

      {/* Above the plan, not among its slots: an approximate callout must not
          reorder bookings or read as a reservation. */}
      {junctions.filter(e => e.date === activeDate).map(e => (
        <div key={`${e.kind}:${e.participantIds.join()}`} className="flex items-center gap-3 mb-3 text-sm font-semibold text-[color:var(--color-itinerary-line)]">
          <AvatarGroup users={travellers.filter(t => e.participantIds.includes(t.id)) as never} size={28} max={3} tapToReveal />
          <span>{e.kind === 'split' ? 'Splitting off' : 'Rejoining'}{e.time ? ` around ${formatClock(e.time)}` : ''}</span>
        </div>
      ))}
      {mode === 'schedule' && schedule && (
        <fieldset disabled={fillingDay !== null} className="min-w-0" aria-busy={saving}>
        <ScheduleView logisticsGaps={logisticsGaps} schedule={viewSchedule} destName={dest.name} headerTints={headerTints} accommodation={accommodation} coordCache={coordCache}
          hasGenerated={legacyGenerated || schedule?.source === 'ai'}
          missingAccommodation={activeDate === travelContext.visit.arrivalDate && missingAccom}
          availableActs={availableActs} travellers={travellers} transports={travelContext.transports} fillingDay={fillingDay} activeDate={activeDate} dayBadge={dayBadge} suggestPrefs={suggestPrefs}
          transportPref={prefs.transport}
          onOpenPreferences={() => { setStep(0); setPendingFill(false); setMode('wizard'); }}
          onAutoFillTrip={autoFillTrip} onFillDay={fillDay} onChange={persistSchedule}
          onExitToItinerary={(activityId) => { handleClose(activityId); onExitToItinerary?.(activityId); }}
          onUncommitPending={uncommitPending} />
        </fieldset>
      )}

      {/* Full-screen and portalled, matching the trip-creation wizard: these are
           the same kind of one-question-at-a-time setup flow, and inline in the
           tab it competed with the date strip and destination header for a
           screen the user is meant to be concentrating on. Portalled rather
           than rendered in place because <main>'s scroll container traps
           position:fixed descendants on iOS PWA (see AGENTS.md), and without
           AnimatePresence around the portal, which has not worked reliably
           here. */}
      {mode === 'wizard' && createPortal(
        <div className="fixed inset-0 z-[70] bg-app-bg/95 backdrop-blur-xl">
          <div className="h-full overflow-y-auto px-6 py-12 pb-32">
            <div className="max-w-md mx-auto">
              <div className="mb-8">
                <div className="flex justify-between items-start gap-4 mb-4">
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-gold mb-1.5">
                      Step {step + 1} · {STEPS[step]}
                    </div>
                    <h2 className="font-serif text-3xl font-bold text-app-text text-balance leading-tight">
                      {STEP_QUESTIONS[step]}
                    </h2>
                  </div>
                  <button aria-label="Close" onClick={() => { setPendingFill(false); setMode('schedule'); }}
                    className="text-app-sub/40 hover:text-app-text transition-colors shrink-0 mt-1">
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <div className="flex items-center gap-1.5">
                  {STEPS.map((s, i) => (
                    <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${i === step ? 'bg-gold' : i < step ? 'bg-gold/40' : 'bg-app-border/30'}`} />
                  ))}
                </div>
              </div>
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.2 }} className="min-h-[200px]">
              {step === 0 && (
                <div className="space-y-5">
                  <Scale5 value={prefs.pace} onChange={v => setPrefs(p => ({ ...p, pace: v }))} left="Relaxed" right="Fast-paced" />
                </div>
              )}
              {step === 1 && (
                <div className="space-y-7">
                  <p className="text-xs text-app-sub/60 leading-relaxed">
                    This sets when each day can start and finish, so nothing lands at a time you would not actually be up for.
                  </p>
                  <TimeSlider
                    label="Usual wake-up"
                    display={formatClock(prefs.wakeTime)}
                    value={timeToMinutes(prefs.wakeTime)}
                    min={WAKE_MIN} max={WAKE_MAX}
                    onChange={v => setPrefs(p => ({ ...p, wakeTime: minutesToTime(v) }))}
                  />
                  <TimeSlider
                    label="Usual bedtime"
                    display={formatClock(prefs.bedTime)}
                    value={bedTimeToSpan(prefs.bedTime)}
                    min={0} max={BED_SPAN_MAX}
                    onChange={v => setPrefs(p => ({ ...p, bedTime: spanToBedTime(v) }))}
                  />
                  <div className="text-sm font-medium text-app-sub -mt-4">
                    About {wakingHours(prefs.wakeTime, prefs.bedTime)} hours to plan across.
                  </div>
                  <div className="space-y-2">
                    <div className="ds-label">Happy with late nights?</div>
                    {LATE_NIGHT_CHOICES.map(([v, label, desc]) => (
                      <button key={v} onClick={() => setPrefs(p => ({ ...p, lateNights: v }))}
                        className={`w-full p-4 rounded-2xl border-2 text-left transition-all ${prefs.lateNights === v ? 'bg-gold/5 border-gold/50' : 'bg-app-faint border-app-border/40 hover:border-gold/30'}`}>
                        <div className="text-sm font-black text-app-text">{label}</div>
                        <div className="text-sm font-medium text-app-sub mt-0.5">{desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {step === 2 && (
                <div className="space-y-4">
                  <p className="text-xs text-app-sub/60">Rate each 1–5 to help us personalise your plan.</p>
                  {INTERESTS.map(({ k, label, desc }) => (
                    <div key={k} className="space-y-1.5">
                      <div>
                        <div className="text-xs font-black text-app-text uppercase tracking-wider">{label}</div>
                        <div className="text-sm text-app-sub">{desc}</div>
                      </div>
                      <Scale5 value={prefs.interests?.[k] ?? 3} onChange={v => setInterest(k, v)} />
                    </div>
                  ))}
                </div>
              )}
              {step === 3 && (
                <div className="space-y-7">
                  <div className="space-y-2">
                    <div className="ds-label">How far on foot between spots?</div>
                    {([['light', 'Light', 'Under 10 min between spots'], ['moderate', 'Moderate', 'Under 20 min between spots'], ['heavy', 'Heavy', 'Up to 30 min between spots']] as const).map(([v, label, desc]) => (
                      <button key={v} onClick={() => setPrefs(p => ({ ...p, walking: v }))}
                        className={`w-full p-4 rounded-2xl border-2 text-left transition-all ${prefs.walking === v ? 'bg-gold/5 border-gold/50' : 'bg-app-faint border-app-border/40 hover:border-gold/30'}`}>
                        <div className="text-sm font-black text-app-text">{label}</div>
                        <div className="text-sm font-medium text-app-sub mt-0.5">{desc}</div>
                      </button>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-semibold text-app-sub">For longer journeys, how would you like to travel?</div>
                    {TRANSPORT_CHOICES.map(([v, label, desc]) => (
                      <button key={v} onClick={() => setPrefs(p => ({ ...p, transport: v }))}
                        className={`w-full p-4 rounded-2xl border-2 text-left transition-all ${prefs.transport === v ? 'bg-gold/5 border-gold/50' : 'bg-app-faint border-app-border/40 hover:border-gold/30'}`}>
                        <div className="text-sm font-black text-app-text">{label}</div>
                        <div className="text-sm font-medium text-app-sub mt-0.5">{desc}</div>
                      </button>
                    ))}
                    {prefs.transport === 'fastest' && (
                      <div className="rounded-xl bg-app-faint/70 border border-app-border/30 px-3.5 py-3 text-sm text-app-sub leading-relaxed">
                        We’ll choose public transport unless a taxi or drive saves significant time.
                      </div>
                    )}
                  </div>
                </div>
              )}
              {step === 4 && (
                <div className="space-y-5">
                  <p className="text-xs text-app-sub/60 leading-relaxed">The upper limit for the plan overall — it can still mix cheap and pricey.</p>
                  <div className="flex gap-2">
                    {([[1, '$', 'Budget-friendly'], [2, '$$', 'Mid-range'], [3, '$$$', 'Premium']] as const).map(([v, sym, label]) => (
                      <button key={v} onClick={() => setPrefs(p => ({ ...p, budget: v as 1 | 2 | 3 }))}
                        className={`flex-1 p-4 rounded-2xl border-2 transition-all ${prefs.budget === v ? 'bg-gold/5 border-gold/50' : 'bg-app-faint border-app-border/40 hover:border-gold/30'}`}>
                        <div className="text-lg font-black text-app-text">{sym}</div>
                        <div className="text-[10px] font-bold text-app-sub/50 uppercase tracking-wider mt-1">{label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
          <div className="flex gap-2 mt-6">
            {step > 0 ? (
              <button onClick={() => setStep(s => s - 1)} className="px-4 py-3.5 rounded-2xl border border-app-border text-app-sub text-[11px] font-black uppercase tracking-widest hover:bg-app-faint transition flex items-center gap-1.5">
                <ChevronLeft className="w-3.5 h-3.5" /> Back
              </button>
            ) : (
              <button onClick={() => { setPendingFill(false); setMode('schedule'); }} className="px-4 py-3.5 rounded-2xl border border-app-border text-app-sub text-[11px] font-black uppercase tracking-widest hover:bg-app-faint transition flex items-center gap-1.5">
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
            )}
            {step < STEPS.length - 1 ? (
              <button onClick={() => setStep(s => s + 1)} className="flex-1 py-3.5 rounded-2xl bg-gold text-white text-[11px] font-black uppercase tracking-widest shadow-lg shadow-gold/20 hover:brightness-110 active:scale-[0.98] transition flex items-center justify-center gap-1.5">
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            ) : pendingFill ? (
              <button onClick={saveAndGenerate} className="flex-1 py-3.5 rounded-2xl bg-gold text-white text-[11px] font-black uppercase tracking-widest shadow-lg shadow-gold/20 hover:brightness-110 active:scale-[0.98] transition flex items-center justify-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> Save & auto-fill
              </button>
            ) : (
              <button onClick={savePrefsOnly} className="flex-1 py-3.5 rounded-2xl bg-gold text-white text-[11px] font-black uppercase tracking-widest shadow-lg shadow-gold/20 hover:brightness-110 active:scale-[0.98] transition flex items-center justify-center gap-1.5">
                <Check className="w-3.5 h-3.5" /> Save preferences
              </button>
            )}
          </div>
            </div>
          </div>
        </div>,
        document.getElementById('import-modal-root') ?? document.body,
      )}
    </div>
  );
}

const fmtDur = (m: number) => {
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
};
const KEY = (import.meta as any).env.VITE_GOOGLE_MAPS_API_KEY || '';

// Floating blurred blobs for the day-header block and the auto-fill banner —
// both sit directly under DestinationStrip with zero gap, so they're
// recoloured to whatever the strip above is showing (headerTints, from
// Itinerary.tsx) instead of the old fixed violet/emerald/terracotta trio,
// which visually fought with whatever destination colour the strip
// happened to be. Two blobs either way: a single destination gets its one
// hue diagonally opposite itself; a transit day (2 tints) splits them
// left/right — departure on the left, arrival on the right — mirroring the
// strip's own split exactly. Same blur/size/animation technique as Home's
// feature card (card-tint-wave-* keyframes, index.css), just recoloured
// and repositioned.
/**
 * A flat wash of the destination strip's colour(s) behind the day's title
 * card, so the card reads as that band continuing rather than as a separate
 * surface. Two colours on a transit day, matching the split strip above.
 *
 * Replaces a set of drifting blobs in the same hues: once the page itself
 * drifts, a second motion inside a short card is noise, and its blobs were
 * mostly edge at this height anyway.
 */
function HeaderTint({ tints }: { tints: string[] }) {
  const wash = tints.length > 1
    ? `linear-gradient(105deg, ${tints[0]} 0%, ${tints[0]} 38%, ${tints[1]} 62%, ${tints[1]} 100%)`
    : tints[0];
  return (
    <div
      className="absolute inset-0 z-0 pointer-events-none"
      aria-hidden="true"
      style={{ background: wash, opacity: 0.16 }}
    />
  );
}

/** Arrival/departure annotations are kept as slim info chips; all other travel
 *  slots are legacy (legs are now DERIVED from stop coordinates, not stored). */
const isTravelAnnotation = (title?: string) => /^(arriv|depart)/i.test(title || '');

/** One-time lazy migration: strip legacy AI travel slots from a saved schedule.
 *  Returns the same object reference when nothing changed (cheap no-op check). */
function pruneTravelSlots(sched: any): any {
  let changed = false;
  const days = (sched.days || []).map((d: any) => {
    const slots = (d.slots || []).filter((s: any) =>
      s.type !== 'transport' &&
      (s.type !== 'travel' || s.journey || isTravelAnnotation(s.title)));

    if (slots.length === (d.slots || []).length) return d;
    changed = true;
    return { ...d, slots };
  });
  return changed ? { ...sched, days } : sched;
}

/** Everything we know about the arriving transport leg, used to synthesise a
 *  realistic step-by-step arrival sequence on the travel day. */
interface ArrivalInfo {
  date: string; time?: string; location?: string;
  clockTransport?: Parameters<typeof stampJourneyClocks>[2];
  durationMin?: number | null;
  /** Arrival time is unknown, even if the route and mode are already saved. */
  assumed?: boolean;
  /** City you're travelling FROM on the arrival day (the previous destination),
   *  for planning the origin morning before a late departure. */
  originCity?: string;
  /** The booked itinerary transport's id — echoed by Gemini on the board/arrive
   *  items so the client can wire the real leg (times, carrier, lock, deep link). */
  transportId?: string;
  dep?: string; arr?: string;
  mode?: string; carrier?: string;
  fromPort?: string; fromAddress?: string; toPort?: string; toCity?: string;
  accomLoc?: string; accomName?: string;
  /** true = same country as the origin, false = different, undefined = unknown.
   *  Drives the pre-departure buffer (international flights need longer). */
  sameCountry?: boolean;
  /** Previous destination's accommodation — the geocodable origin for the leg
   *  to the departure port, when you're leaving from the prior destination. */
  prevAccomLoc?: string; prevAccomName?: string;
}

type LegKind = 'flight' | 'train' | 'bus' | 'ferry' | 'car' | 'other';
/** Mode (+ whether it's an international hop) → pre-departure buffer (reach the
 *  port + check in) and arrival-clearance buffer, in minutes, plus the icon kind
 *  and the port noun. Flights: 120m domestic / 180m international. Land/sea legs:
 *  20m domestic, more if crossing a border. Car/taxi: none. */
/**
 * What we assume when the itinerary has no transport into or out of a
 * destination. A day with no known boundary used to be planned as a full day
 * in place, which is how a schedule ends up offering breakfast at 8am in a
 * city you are still flying to. Midday is the honest middle: it costs the
 * traveller a morning they may have had, rather than inventing one they
 * certainly did not.
 */
const ASSUMED_ARRIVAL = '12:00';
const ASSUMED_DEPARTURE = '12:00';

function arrivalLeg(mode?: string, sameCountry?: boolean): { kind: LegKind; pre: number; clear: number; port: string } {
  const m = (mode || '').toLowerCase();
  const intl = sameCountry === false; // undefined → treat as domestic
  if (/flight|plane|air/.test(m)) return { kind: 'flight', pre: intl ? 180 : 120, clear: intl ? 60 : 40, port: 'airport' };
  if (/train|rail/.test(m))       return { kind: 'train', pre: intl ? 45 : 20, clear: intl ? 20 : 10, port: 'station' };
  if (/bus|coach/.test(m))        return { kind: 'bus', pre: intl ? 40 : 20, clear: intl ? 15 : 5, port: 'station' };
  if (/ferry|boat|ship/.test(m))  return { kind: 'ferry', pre: intl ? 60 : 30, clear: 15, port: 'terminal' };
  if (/car|drive|driving|taxi|uber|ride/.test(m)) return { kind: 'car', pre: 0, clear: 0, port: '' };
  return { kind: 'other', pre: intl ? 45 : 20, clear: 15, port: 'terminal' };
}

/** Build the arrival-day journey as ITEMS (places/events) separated by interceding
 *  transport legs — a card NEVER describes a journey. Pattern:
 *    Check out  →(transfer leg)→  Depart · [station]  →(booked leg)→  Arrive · [terminal]  →(transfer leg)→  Check in
 *  The accommodation→station transfer and the terminal→accommodation transfer are
 *  derived automatically (geocodable endpoints); the booked flight/train rides on
 *  the boarding item's `legAfter`. Terminal clearance folds INTO the Arrive item.
 *  Times anchor to dep/arr, snapped onto the 5-minute grid. */
function buildArrivalJourney(a: ArrivalInfo, destName: string): any[] {
  // Unknown arrival time: keep provisional arrival and check-in visible.
  if (a.assumed) {
    const arrMin = toMin(a.time || ASSUMED_ARRIVAL);
    return [
      {
        id: uid(), type: 'travel', legKind: 'other' as LegKind, placeholder: true,
        title: `Arrive in ${destName}`, start: toHHMM(round5(arrMin)), durationMin: 30,
        subtitle: 'Arrival time assumed — add your transport in Itinerary to plan around the real one',
      },
      {
        id: uid(), type: 'travel', legKind: 'other' as LegKind, placeholder: !a.accomLoc,
        title: a.accomName ? `Check in at ${a.accomName}` : 'Check in at your accommodation',
        start: toHHMM(round5(arrMin + 60)), durationMin: 30,
        subtitle: a.accomLoc
          ? 'Drop your bags & settle in'
          : 'Add your accommodation in Itinerary and travel to and from it is worked out for you',
        ...(a.accomLoc ? { location: a.accomLoc } : {}),
      },
    ].map(s => ({ ...s, journey: true, locked: false }));
  }
  const info = arrivalLeg(a.mode, a.sameCountry);
  const depMin = a.dep ? toMin(a.dep) : null;
  const arrMin = a.arr ? toMin(a.arr) : (a.time ? toMin(a.time) : null);
  if (arrMin == null) return [];
  const out: any[] = [];

  // Car / taxi: a single door-to-door drive to the accommodation — no terminal,
  // so one Arrive item is the whole journey.
  if (info.kind === 'car') {
    out.push({ id: uid(), type: 'travel', legKind: info.kind, title: `Arrive in ${destName}`, start: toHHMM(round5(arrMin)), durationMin: 30, subtitle: 'Settle in at your accommodation', ...(a.accomLoc ? { location: a.accomLoc } : {}) });
    return out.map(s => ({ ...s, journey: true, locked: true }));
  }

  const port = info.port || 'terminal';
  const terminalLoc = a.location || a.toPort;

  const kindNoun = info.kind === 'flight' ? 'flight' : info.kind === 'train' ? 'train' : info.kind === 'ferry' ? 'ferry' : 'service';
  if (depMin != null) {
    // 1) Check out of the origin accommodation — a PLACE, not a journey. The
    //    accommodation → station transfer is then drawn automatically as the
    //    interceding leg to the boarding item (both ends geocodable).
    const accomLocO = a.prevAccomLoc || a.fromAddress;
    if (a.prevAccomName || accomLocO) {
      out.push({
        id: uid(), type: 'travel', legKind: info.kind,
        title: a.prevAccomName ? `Pack & check out of ${a.prevAccomName}` : 'Pack & check out of your accommodation',
        start: toHHMM(round5(Math.max(0, depMin - info.pre - 15))), durationMin: 15,
        subtitle: 'Settle your bill & gather your bags',
        ...(accomLocO ? { location: accomLocO } : {}),
      });
    }
    // 2) Board at the departure port — the booked flight/train rides on `legAfter`
    //    so it renders as the next interceding leg, never as a card.
    out.push({
      id: uid(), type: 'travel', legKind: info.kind,
      title: a.fromPort ? `Depart · ${a.fromPort}` : `Depart for ${destName}`,
      start: toHHMM(round5(depMin)), durationMin: 0,
      subtitle: a.carrier ? `Check in & board your ${a.carrier}` : `Check in & board your ${kindNoun}`,
      legAfter: {
        kind: info.kind,
        durationMin: a.durationMin === undefined ? round5(Math.max(5, arrMin - depMin)) : (a.durationMin ?? 0),
        ...(a.transportId ? { linkedId: a.transportId } : {}),
        label: a.carrier ? `${a.carrier} to ${a.toCity || destName}` : `${info.kind === 'flight' ? 'Flight' : info.kind === 'train' ? 'Train' : info.kind === 'ferry' ? 'Ferry' : 'Travel'} to ${a.toCity || destName}`,
        ...(a.fromPort && a.toPort ? { route: `${a.fromPort} → ${a.toPort}` } : {}),
      },
      ...(a.fromPort ? { location: a.fromPort } : (a.fromAddress ? { location: a.fromAddress } : {})),
    });
  }

  // 2) Arrive item — terminal clearance folded in. Its location is the TERMINAL
  //    so the transfer leg to the accommodation geocodes correctly.
  out.push({
    id: uid(), type: 'travel', legKind: info.kind,
    title: `Arrive in ${destName}${a.toPort ? ` · ${a.toPort}` : ''}`,
    start: toHHMM(round5(arrMin)), durationMin: info.clear,
    subtitle: `Clear the ${port} & collect your bags`,
    ...(terminalLoc ? { location: terminalLoc } : {}),
  });

  // 3) Check-in item — the transfer from terminal → here is the interceding leg
  //    derived between item 2's terminal location and this accommodation.
  if (a.accomLoc || a.accomName) {
    out.push({
      id: uid(), type: 'travel', legKind: info.kind,
      title: a.accomName ? `Check in at ${a.accomName}` : 'Check in at your accommodation',
      start: toHHMM(round5(arrMin + info.clear + 30)), durationMin: 30,
      subtitle: 'Drop your bags & settle in',
      ...(a.accomLoc ? { location: a.accomLoc } : {}),
    });
  }

  return out.map(s => ({ ...s, journey: true, locked: true, ...(a.transportId ? { journeyTransportId: a.transportId } : {}) }));
}

/** Wire Gemini's tagged journey items (kind / transportId) into real journey
 *  slots. Gemini owns the prose, locations and structure; the client stamps the
 *  AUTHORITATIVE booked facts: exact times, the booked leg (with its deep link),
 *  and the lock. Non-journey activities pass through untouched. */
function wireJourney(slots: any[], a: ArrivalInfo, destName: string): any[] {
  const info = arrivalLeg(a.mode, a.sameCountry);
  const depMin = a.dep ? toMin(a.dep) : null;
  const arrMin = a.arr ? toMin(a.arr) : (a.time ? toMin(a.time) : 0);
  const legNoun = info.kind === 'flight' ? 'Flight' : info.kind === 'train' ? 'Train' : info.kind === 'ferry' ? 'Ferry' : info.kind === 'bus' ? 'Bus' : 'Travel';
  return slots.map(s => {
    if (!s.kind) return s;
    // Surface Gemini's prose on the journey card (the travel card reads `subtitle`).
    const base: any = { ...s, journeyRole: s.kind, ...(a.transportId ? { journeyTransportId: a.transportId } : {}), type: 'travel', legKind: info.kind, journey: true, locked: true, subtitle: s.subtitle || s.desc };
    delete base.kind; delete base.transportId;
    switch (s.kind) {
      case 'checkout':
        return { ...base, start: toHHMM(round5(Math.max(0, (depMin ?? arrMin) - info.pre - 15))), durationMin: 15 };
      case 'board':
        return {
          ...base, start: toHHMM(round5(depMin ?? arrMin)), durationMin: 0,
          legAfter: {
            kind: info.kind,
            durationMin: a.durationMin === undefined ? ((depMin != null && arrMin) ? round5(Math.max(5, arrMin - depMin)) : 0) : (a.durationMin ?? 0),
            label: a.carrier ? `${a.carrier} to ${a.toCity || destName}` : `${legNoun} to ${a.toCity || destName}`,
            // No raw route line — the board/arrive cards already name the stations
            // (in Gemini's clean Title Case, not the itinerary's raw string).
            ...(a.transportId ? { linkedId: a.transportId } : {}),
          },
        };
      case 'arrive':
        return { ...base, start: toHHMM(round5(arrMin)), durationMin: info.clear };
      case 'checkin':
        return { ...base, start: toHHMM(round5(arrMin + info.clear + 30)), durationMin: 30 };
      default:
        return base;
    }
  });
}

/** Post-process a fresh AI generation. On a travel day, Gemini drafts the journey
 *  items (tagged kind/transportId) and the client wires the booked facts onto them
 *  (wireJourney); if it emitted no tagged journey, we fall back to synthesising one
 *  deterministically (buildArrivalJourney). Untagged logistics-looking slots are
 *  dropped, and the departure chip is appended on the departure day. */
function postProcessGenerated(raw: any, destName: string, arrival?: ArrivalInfo, departure?: { date: string; time?: string; assumed?: boolean }, dayWindow?: { wakeTime: string; bedTime: string }): any {
  const byStart = compareScheduleTime;
  const isJourneyKind = (k: any) => /^(checkout|board|arrive|checkin)$/.test(k || '');
  // Drop untagged slots that merely *look* like logistics (a safety net against
  // the model narrating travel) — tagged journey items are always kept.
  const isLogisticsDupe = (s: any) => /^(arriv|depart|check[\s-]?in|leave|transfer|travel|head to|get to|make your way)\b/i.test((s.title || '').trim());
  const days = (raw?.days || []).map((d: any) => {
    const onArrivalDay = arrival && d.date === arrival.date;
    const onDepartureDay = departure?.time && d.date === departure.date;
    const rawSlots = (d.slots || []).filter((s: any) =>
      !(onArrivalDay && !arrival?.dep && /^(checkout|board)$/.test(s.kind || '')));
    const hasJourneyTags = !!onArrivalDay && rawSlots.some((s: any) => isJourneyKind(s.kind));
    let slots = rawSlots
      .filter((s: any) => s.type !== 'travel')
      // Strip untagged logistics narration on travel days; keep tagged journey items.
      .filter((s: any) => !((onArrivalDay || onDepartureDay) && !s.kind && isLogisticsDupe(s)))
      // Snap every model start time onto the 5-minute grid.
      .map((s: any) => ({ id: uid(), ...s, ...(s.start ? { start: snap5(s.start) } : {}) }));
    if (onArrivalDay && arrival?.clockTransport) slots = stampJourneyClocks(slots, d.date, arrival.clockTransport);
    if (hasJourneyTags)
      slots = wireJourney(slots, arrival!, destName);
    else if (onArrivalDay && arrival)
      slots = [...buildArrivalJourney(arrival, destName), ...slots];  // fallback
    if (onDepartureDay)
      slots = [...slots, { id: uid(), type: 'travel', journey: true, locked: !departure!.assumed,
        title: `Depart ${destName}`, start: snap5(departure!.time!), durationMin: 0,
        ...(departure!.assumed ? { placeholder: true, subtitle: 'Departure time assumed — add a departure time in Itinerary' } : {}) }];
    if (onArrivalDay && arrival?.clockTransport) slots = stampJourneyClocks(slots, d.date, arrival.clockTransport);
    slots = slots.sort(byStart);
    // The prompt states the traveller's waking hours as a hard constraint, but
    // the model treats them as a suggestion — this is where the answer is made
    // true. Journey items are exempt: a 06:00 flight is a 06:00 flight.
    if (dayWindow) slots = clampToDayWindow(slots, dayWindow.wakeTime, dayWindow.bedTime);
    return { ...d, slots };
  });
  return { ...raw, days, source: 'ai' };
}

/**
 * Shortlist items marked "Committed to Plan" (inPlan: true) are sent to
 * Gemini as MUST-INCLUDE (see the prompt's "Schedule ALL must-include
 * items" instruction, travelServices.ts) — but that's a prompt hint, not a
 * guarantee; the model can still drop one. This is the actual guarantee:
 * after generation, any committed item missing from every day's slots gets
 * one inserted directly, spread across the least-loaded days rather than
 * piled onto a single one.
 */
function ensureMustIncludeSlots(generated: any, mustInclude: { id: string; name: string; type: string }[]): any {
  if (!mustInclude.length || !generated?.days?.length) return generated;
  const placed = new Set<string>();
  generated.days.forEach((d: any) => (d.slots || []).forEach((s: any) => { if (s.activityId) placed.add(s.activityId); }));
  const missing = mustInclude.filter(a => !placed.has(a.id));
  if (!missing.length) return generated;

  const days = generated.days.map((d: any) => ({ ...d, slots: [...(d.slots || [])] }));
  missing.forEach(a => {
    // Least-loaded day (by non-travel slot count) so several missing items
    // spread out rather than stacking on the same day.
    let targetIdx = 0;
    let targetCount = Infinity;
    days.forEach((d: any, i: number) => {
      const count = (d.slots || []).filter((s: any) => s.type !== 'travel').length;
      if (count < targetCount) { targetCount = count; targetIdx = i; }
    });
    const target = days[targetIdx];
    const nonTravel = (target.slots || []).filter((s: any) => s.type !== 'travel');
    const lastEnd = nonTravel.length
      ? Math.max(...nonTravel.map((s: any) => toMin(s.start || '09:00') + (s.durationMin || 0)))
      : toMin('10:00');
    const start = toHHMM(round5(Math.min(lastEnd + 30, toMin('20:00'))));
    target.slots = [...(target.slots || []), {
      id: uid(),
      type: ACT_SLOT_TYPE[a.type] || 'experiences',
      activityId: a.id,
      title: a.name,
      start,
      durationMin: 90,
      ...(target.slots.find((s: any) => s.clock)?.clock ? { clock: target.slots.filter((s: any) => s.clock).at(-1).clock } : {}),
    }].sort(compareScheduleTime);
  });
  return { ...generated, days };
}

/**
 * Hard-guarantee that locked, non-journey slots (booked imports, or anything
 * the traveller manually pinned) survive a full regenerate at their exact
 * time — the `existing` field sent to Gemini is only a prompt hint, not a
 * promise, so this is what makes "immovable" actually true. Clears any
 * AI-generated slot that overlaps a locked slot's time window (same overlap
 * rule already used to protect travel estimates), then puts the original
 * locked slot objects back in verbatim. Days/dates with no locked slots are
 * untouched.
 */
function reserveLockedSlots(days: any[], lockedByDate: Map<string, any[]>): any[] {
  if (!lockedByDate.size) return days;
  return days.map(day => {
    const locked = lockedByDate.get(day.date);
    if (!locked?.length) return day;
    const cleared = reserveEstimatedTravel(day.slots || [],
      locked.map((s: any) => ({ start: s.start, durationMin: s.durationMin, clock: s.clock })) as any);
    return { ...day, slots: [...cleared, ...locked].sort(compareScheduleTime) };
  });
}

/** Great-circle distance in km (good enough to pick a leg mode + estimate). */
function kmBetween(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371, toRad = (x: number) => x * Math.PI / 180;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

type LegMode = 'walking' | 'transit' | 'driving';
// Per-mode model for the straight-line estimate:
//  - base:   fixed overhead minutes (waiting for/walking to transit; parking).
//  - detour: multiplier applied to straight-line km to approximate real path.
const MODE_INFO: Record<LegMode, { Icon: LucideIcon; word: string; base: number; detour: number }> = {
  walking: { Icon: Footprints, word: 'walk',    base: 0, detour: 1.25 },
  transit: { Icon: TramFront,  word: 'transit', base: 7, detour: 1.30 },
  driving: { Icon: Car,        word: 'drive',   base: 3, detour: 1.35 },
};
// Effective door-to-door speed (km/h). Crucially this scales with distance for
// transit and driving: a short inner-city hop is stop-heavy and slow, while a
// long leg rides express rail / faster roads — so a flat speed badly over-
// estimated long legs (e.g. CDG → Le Marais on the RER). Walking stays flat.
function effKmh(mode: LegMode, km: number): number {
  if (mode === 'walking') return 4.8;
  if (mode === 'transit') return Math.min(30, Math.max(15, 11 + km * 1.4));
  return Math.min(75, Math.max(22, 20 + km * 1.2)); // driving
}
// Gemini's per-leg travelMode (walk | transit | car) → a Maps travel mode.
const GEMINI_MODE: Record<string, LegMode> = { walk: 'walking', transit: 'transit', car: 'driving' };
// Distance-based mode guess for the instant placeholder only (the real mode
// comes from fetchLegRoute). Driving is the safe default — universally
// available; the real route upgrades to transit only where it genuinely wins.
const autoMode = (km: number): LegMode => km < 1.2 ? 'walking' : 'driving';
// Round a duration up to the nearest 5 minutes (min 5).
const roundUp5 = (m: number) => Math.max(5, Math.ceil(m / 5) * 5);

/** Leg chip estimate. Mode precedence: Gemini's explicit choice → a distance
 *  guess from resolved coords → transit. The door-to-door duration is computed
 *  per-mode (each has its own speed, overhead and detour factor) and rounded up
 *  to the nearest 5 min once both ends have coordinates; before that we still
 *  show the mode (icon + word), and the expanded route map shows the detail. */
function legEstimate(from?: { lat: number; lng: number }, to?: { lat: number; lng: number }, preferMode?: LegMode):
  { mode: LegMode; Icon: LucideIcon; label: string; durationMin: number } {
  const km = (from && to) ? kmBetween(from, to) : null;
  const mode = preferMode ?? (km != null ? autoMode(km) : 'transit');
  const info = MODE_INFO[mode];
  if (km == null) return { mode, Icon: info.Icon, label: info.word, durationMin: 0 };
  // Leading "Est." flags that the time is a straight-line approximation, not a
  // real routed duration.
  const m = roundUp5(info.base + (km * info.detour) / effKmh(mode, km) * 60);
  return { mode, Icon: info.Icon, label: `Est. ${fmtDur(m)} ${info.word}`, durationMin: m };
}

/** Build a Google Maps Embed directions URL. */
function embedDirectionsUrl(params: {
  origin: string; destination: string; waypoints?: string[]; mode?: string;
}): string {
  const enc = encodeURIComponent;
  const wps = (params.waypoints || []).slice(0, 9);
  const wp  = wps.length ? `&waypoints=${wps.map(enc).join('|')}` : '';
  const mode = (params.mode || 'driving').toLowerCase();
  return `https://www.google.com/maps/embed/v1/directions?key=${KEY}&origin=${enc(params.origin)}&destination=${enc(params.destination)}${wp}&mode=${mode}`;
}

// ── Real routed leg durations via DirectionsService ────────────────────────
// legEstimate (above) is a straight-line guess — fine as an instant
// placeholder, wrong as a shown fact (Canggu's lanes turn a "20 min" guess
// into a real 46 min). This asks Google for the actual routed time and picks
// the mode: Gemini's "walk" is honoured for close hops; otherwise transit
// wins unless driving is >1.5x faster (or transit has no route at all — the
// common case outside big-city metros). Cached module-wide by rounded
// coords so day-flipping doesn't re-bill the same leg.
export type LegRoute = { mode: LegMode; durationMin: number; result: any };
const legRouteCache = new Map<string, Promise<LegRoute | null>>();

// transportPref is part of the key: the same two points resolve to a different
// mode depending on it, so caching without it would serve one traveller's
// answer to another — or to the same traveller after they changed it.
function legRouteKey(a: { lat: number; lng: number }, b: { lat: number; lng: number }, prefer?: LegMode, transportPref?: TransportPref): string {
  return `${a.lat.toFixed(5)},${a.lng.toFixed(5)}|${b.lat.toFixed(5)},${b.lng.toFixed(5)}|${prefer || ''}|${transportPref || ''}`;
}

function fetchLegRoute(
  a: { lat: number; lng: number }, b: { lat: number; lng: number },
  prefer?: LegMode, when?: Date, transportPref: TransportPref = 'fastest',
): Promise<LegRoute | null> {
  const key = legRouteKey(a, b, prefer, transportPref);
  const cached = legRouteCache.get(key);
  if (cached) return cached;
  const p = (async (): Promise<LegRoute | null> => {
    try {
      await loadGoogleMaps();
      const g = (window as any).google;
      if (!g?.maps?.DirectionsService) return null;
      const ds = new g.maps.DirectionsService();
      const route = (travelMode: string, extra: Record<string, any> = {}) =>
        new Promise<any>(res => {
          ds.route({ origin: a, destination: b, travelMode, ...extra }, (r: any, st: any) =>
            res(st === g.maps.DirectionsStatus.OK ? r : null));
        });
      const durMin = (r: any): number | null =>
        r?.routes?.[0]?.legs?.[0]?.duration?.value != null
          ? Math.round(r.routes[0].legs[0].duration.value / 60)
          : null;

      // Close hop Gemini flagged as walking — honour it only if the real walk
      // is genuinely short (≤25 min). A "1h 10m walk" is a bad call; fall
      // through to transit/driving for those.
      if (prefer === 'walking') {
        const w = await route('WALKING');
        const wm = durMin(w);
        if (wm != null && wm <= 25) return { mode: 'walking', durationMin: wm, result: w };
      }

      const [driving, transit] = await Promise.all([
        route('DRIVING'),
        route('TRANSIT', { transitOptions: { departureTime: when || new Date() } }),
      ]);
      const dMin = durMin(driving), tMin = durMin(transit);
      // Which of the two wins is the traveller's call, not a fixed rule — see
      // chooseTransitOrDriving for what each preference actually means.
      const chosen = chooseTransitOrDriving(tMin, dMin, transportPref);
      if (chosen === 'transit' && tMin != null) return { mode: 'transit', durationMin: tMin, result: transit };
      if (chosen === 'driving' && dMin != null) return { mode: 'driving', durationMin: dMin, result: driving };
      return null;
    } catch {
      return null;
    }
  })();
  legRouteCache.set(key, p);
  // A null is a failure or a genuine "no route", and the two look the same
  // here. Held for a minute so the burst of renders asking for this leg shares
  // one lookup, then released — otherwise one failed load of the Maps library,
  // or one rate-limited moment, was every leg's answer for the whole session,
  // and the day's travel times stayed on estimates until a reload.
  p.then(route => {
    if (route === null) setTimeout(() => { if (legRouteCache.get(key) === p) legRouteCache.delete(key); }, 60_000);
  });
  return p;
}

// Clean, chrome-free route map — a styled google.maps.Map with a
// DirectionsRenderer, replacing the Embed API iframe (which always drags in
// Google's route panel, "keyboard shortcuts" text and a floating place
// card). `result` is a DirectionsResult already fetched by fetchLegRoute.
export function LegRouteMap({ result }: { result: any }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !result) return;
    const g = (window as any).google;
    if (!g?.maps?.Map) return;
    // A preview, not a thing to pan — tapping it opens the directions
    // chooser instead, so all gestures are off.
    const map = new g.maps.Map(el, {
      disableDefaultUI: true,
      zoomControl: false,
      gestureHandling: 'none',
      clickableIcons: false,
      keyboardShortcuts: false,
    });
    const dr = new g.maps.DirectionsRenderer({
      map,
      suppressInfoWindows: true,
      polylineOptions: { strokeColor: '#c5a059', strokeOpacity: 0.9, strokeWeight: 5 },
      markerOptions: { opacity: 0.9 },
    });
    dr.setDirections(result);
    return () => { dr.setMap(null); };
  }, [result]);
  return <div ref={ref} className="w-full h-full" />;
}

// iOS gets an "Open in Apple Maps" option alongside Google Maps.
const isIOS = typeof navigator !== 'undefined'
  && (/iP(hone|ad|od)/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1));
const APPLE_DIRFLG: Record<LegMode, string> = { walking: 'w', transit: 'r', driving: 'd' };
function googleMapsDirUrl(a: { lat: number; lng: number }, b: { lat: number; lng: number }, mode: LegMode): string {
  return `https://www.google.com/maps/dir/?api=1&origin=${a.lat},${a.lng}&destination=${b.lat},${b.lng}&travelmode=${mode}`;
}
function appleMapsDirUrl(a: { lat: number; lng: number }, b: { lat: number; lng: number }, mode: LegMode): string {
  return `https://maps.apple.com/?saddr=${a.lat},${a.lng}&daddr=${b.lat},${b.lng}&dirflg=${APPLE_DIRFLG[mode]}`;
}

const uid = () => Math.random().toString(36).slice(2, 9);
// Map an itinerary activity's type onto a schedule slot type/icon.
const ACT_SLOT_TYPE: Record<string, string> = { attraction: 'experiences', eat: 'food', nightlife: 'nightlife', shopping: 'shopping' };

// Slot types offered in the manual editor's type picker. Transport and
// Accommodation are excluded: transport lives in the connector between slots,
// and lodging is the trip's accommodation (not a schedule activity).
const TYPE_CHOICES: { v: string; label: string }[] = [
  ...ITINERARY_L1.filter(c => c.key !== 'transport' && c.key !== 'accommodation').map(c => ({ v: c.key, label: c.label })),
  { v: 'free', label: 'Free time' },
  { v: 'other', label: 'Other' },
];

// "HH:MM" ↔ minutes-since-midnight.
const toMin = (t: string) => { const [h, m] = (t || '').split(':').map(Number); return (isNaN(h) ? 0 : h) * 60 + (isNaN(m) ? 0 : m); };
const toHHMM = (min: number) => { const x = Math.max(0, Math.min(24 * 60 - 1, Math.round(min))); return `${String(Math.floor(x / 60)).padStart(2, '0')}:${String(x % 60).padStart(2, '0')}`; };
// Round a minute value UP to the nearest 5 (all displayed start times sit on a
// :00/:05/:10… grid). Idempotent for values already on the grid.
const round5 = (min: number) => Math.ceil(min / 5) * 5;
// Normalise an "HH:MM" start string onto the 5-minute grid.
const snap5 = (t: string) => toHHMM(round5(toMin(t)));

// Minimum free stretch (minutes) that earns an inline "get ideas" prompt.
const GAP_MIN = 90;
// Above this, an auto leg estimate is treated as implausible for a same-day hop
// (almost always a bad geocode) and we show a manual "Add transport" line instead.
const MAX_AUTO_LEG_MIN = 240;
// Ideas-tab categories — derived from the canonical L1 taxonomy so labels/icons
// stay in sync automatically. Only itinerary-relevant L1s that have a matching
// recommendation endpoint are included.
const SUGGEST_KINDS = [
  { key: 'experiences', label: L1_BY_KEY.experiences.label, recType: 'attractions' as const, arr: 'attractions' as const },
  { key: 'food',        label: L1_BY_KEY.food.label,        recType: 'food'        as const, arr: 'eats'        as const },
  { key: 'nightlife',   label: L1_BY_KEY.nightlife.label,   recType: 'nightlife'   as const, arr: 'nightlife'   as const },
  { key: 'shopping',    label: L1_BY_KEY.shopping.label,    recType: 'shopping'    as const, arr: 'shopping'    as const },
];
type SuggestKind = typeof SUGGEST_KINDS[number];
// Slot type → recommendation endpoint, for fetching description-based suggestions
// on a locationless entry. Food/meal → food, nightlife → nightlife, shopping →
// shopping, else attractions.
const WELLNESS_PLACE_RE = /\b(spa|wellness|massage|bathhouse|sauna|hot springs?)\b/i;
function needsSpotSuggestions(s: any, destination: string): boolean {
  if (s.placeId) return false;
  const location = (s.location || '').trim().toLowerCase();
  const area = (s.title || '').match(/\b(?:in|around)\s+(.+)$/i)?.[1]?.trim().toLowerCase();
  return !location || WELLNESS_PLACE_RE.test(s.title || '')
    || location === destination.trim().toLowerCase() || location === area
    || !!area && location.startsWith(`${area},`)
    || /^(?:(?:local|nearby)\s+)?(?:caf[eé]s?|restaurants?|shops?|bars?)(?:\s+in\b|$)/i.test(location);
}
const isUnsuitableBreakfastOption = (slotTitle: string, option: { title?: string; desc?: string }): boolean =>
  /\bbreakfast\b/i.test(slotTitle) && /\bbutcher(?:y)?\b|\bmeat (?:co\b|company\b|market\b|shop\b)/i.test(`${option.title || ''} ${option.desc || ''}`);
const recTypeForSlot = (t?: string, title = ''): 'attractions' | 'food' | 'nightlife' | 'shopping' => {
  if (WELLNESS_PLACE_RE.test(title)) return 'nightlife';
  const key = normalizeSlotType(t);
  return key === 'food' || key === 'nightlife' || key === 'shopping' ? key : 'attractions';
};

/** Recompute the day's timeline from `editedId` onward so it stays watertight:
 *  each UNLOCKED item starts when the previous one ends plus the travel leg
 *  between them (timestamp + duration + travel = next timestamp). LOCKED items
 *  keep their fixed start and anchor the chain — if the running cursor overruns a
 *  locked start that's an unavoidable overlap, surfaced separately. Items before
 *  the edited one are left untouched. legMin(a,b) → travel minutes (0 if unknown).
 */
/** Re-lay an ALREADY-SEQUENCED day from `startIdx` forward, keeping each item's
 *  own duration and the travel leg before it. The sequence is taken as given —
 *  this is the half of cascadeTimes that must not re-sort, because a reorder
 *  hands it a list whose positions are correct and whose times are not yet. */
export function relayFrom(ordered: any[], startIdx: number, legMin: (a: any, b: any) => number): any[] {
  if (startIdx < 0 || startIdx >= ordered.length) return ordered;
  const out = [...ordered];
  let cursor = scheduleMinute(out[startIdx]) + (out[startIdx].durationMin || 0);
  for (let i = startIdx + 1; i < out.length; i++) {
    const prev = out[i - 1];
    const s = out[i];
    const arrive = cursor + legMin(prev, s);
    if (s.locked) {
      cursor = scheduleMinute(s) + (s.durationMin || 0); // fixed anchor
    } else {
      out[i] = shiftScheduleTime(s, arrive);
      cursor = arrive + (s.durationMin || 0);
    }
  }
  return out;
}

function cascadeTimes(slots: any[], editedId: string, legMin: (a: any, b: any) => number): any[] {
  const ordered = [...slots].sort(compareScheduleTime);
  const idx = ordered.findIndex(s => s.id === editedId);
  if (idx < 0) return slots;
  return relayFrom(ordered, idx, legMin);
}

/** Nearest-neighbour reorder of a day's geocoded activity slots, anchored at the
 *  accommodation. Keeps the day's start-time skeleton: the times currently held
 *  by reorderable stops are reassigned, earliest-first, to the new sequence.
 *  Slots with no coords (meals, free time, annotations) keep their time.
 *  Locked and journey slots (a booked flight, a traveller-pinned time) are
 *  excluded even when geocoded — they're facts, not suggestions, and this
 *  would otherwise happily hand "Arrive in Brisbane · BNE" someone else's
 *  start time. They keep their own position and time; the reorder only ever
 *  touches the stops actually free to move. */
function optimizeDayOrder(slots: any[], coordOf: (s: any) => { lat: number; lng: number } | undefined, accom?: { lat: number; lng: number }): any[] {
  // Stops in different localities must not trade places across a booked journey.
  if (new Set(slots.map(s => s.clock?.zone).filter(Boolean)).size > 1) return slots;
  const movable = slots.map((s, i) => ({ s, i })).filter(x => !x.s.locked && coordOf(x.s));
  if (movable.length < 3) return slots;
  const times = movable.map(x => x.s.start).sort((a, b) => toMin(a) - toMin(b));
  const remaining = [...movable];
  const order: typeof movable = [];
  let cur = accom ?? coordOf(movable[0].s)!;
  while (remaining.length) {
    let best = 0, bestD = Infinity;
    remaining.forEach((x, k) => { const dKm = kmBetween(cur, coordOf(x.s)!); if (dKm < bestD) { bestD = dKm; best = k; } });
    const picked = remaining.splice(best, 1)[0];
    order.push(picked); cur = coordOf(picked.s)!;
  }
  const next = [...slots];
  order.forEach((x, k) => { next[x.i] = { ...x.s, start: times[k] }; });
  return next.sort(compareScheduleTime);
}

/** Star rating display (read-only, fractional). */
function StarRating({ rating }: { rating: number }) {
  const full = Math.floor(rating);
  const frac = rating - full;
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(n => (
        <Star key={n} className={`w-2.5 h-2.5 ${n <= full ? 'text-amber-400 fill-amber-400' : n === full + 1 && frac >= 0.5 ? 'text-amber-400 fill-amber-400/50' : 'text-app-border/60'}`} />
      ))}
      <span className="text-[10px] font-bold text-app-sub/60 ml-0.5">{rating.toFixed(1)}</span>
    </div>
  );
}

/** Add-spot bottom panel: pick from your plan, your shortlist, or fresh AI
 *  Ideas (which also get written back to the trip's itinerary shortlist). */
function AddSpotPanel({ shortlist, destName, suggestPrefs, seedStart, initialTab, onAddDraft, onAddSuggestion, onViewSaved, onClose }: {
  shortlist: any[]; destName: string; suggestPrefs: string;
  seedStart?: string; initialTab?: 'new' | 'shortlist' | 'ideas';
  onAddDraft: (slot: any) => void;
  onAddSuggestion: (rec: Recommendation, kind: SuggestKind) => Promise<void>;
  /** Opens a saved Shortlist item in its portal view, so you can read the
   *  full entry before committing it to a time slot. */
  onViewSaved?: (activityId: string) => void;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<'new' | 'shortlist' | 'ideas'>(initialTab || 'new');
  // kind stays null until the user picks a category — no Gemini call before then.
  const [kind, setKind] = useState<SuggestKind | null>(null);
  const [ideas, setIdeas] = useState<Record<string, Recommendation[]>>({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [added, setAdded] = useState<Set<string>>(new Set());

  const fetchIdeas = async (k: SuggestKind) => {
    if (ideas[k.key]) return;
    setLoading(true); setErr(null);
    try {
      const recs = await getRecommendations(destName, k.recType, suggestPrefs);
      setIdeas(prev => ({ ...prev, [k.key]: recs }));
    } catch (e: any) {
      setErr(/quota|exhausted|429/i.test(e?.message || e?.code || '') ? 'Suggestions are busy right now — try again shortly.' : 'Couldn\'t load ideas. Try again.');
    } finally { setLoading(false); }
  };
  const selectKind = (k: SuggestKind) => { setKind(k); fetchIdeas(k); };

  const tabCls = (on: boolean) => `shrink-0 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 transition-colors whitespace-nowrap ${on ? 'bg-gold text-white shadow-sm' : 'bg-white border border-app-border/50 text-app-sub/60'}`;

  // No outer well / close button of its own — this renders inside SpotModal,
  // which supplies the surface, the title and the X.
  return (
    <div className="space-y-4">
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1">
        {/* Icons match how each source is signed elsewhere in the app: a
            star for the Shortlist (its tab icon + the pending-items CTA),
            sparkles for Talisman Explorer (the free-time CTA that routes
            straight into this tab). */}
        <button onClick={() => setTab('new')} className={tabCls(tab === 'new')}><Plus className="w-3 h-3" /> New spot</button>
        <button onClick={() => setTab('shortlist')} className={tabCls(tab === 'shortlist')}><Star className="w-3 h-3" fill="currentColor" /> Shortlist</button>
        <button onClick={() => setTab('ideas')} className={tabCls(tab === 'ideas')}><Sparkles className="w-3 h-3" /> Talisman Explorer</button>
      </div>

      {tab === 'new' ? (
        <SlotForm mode="add" destName={destName}
          initial={seedStart ? { start: seedStart } : {}}
          onSubmit={onAddDraft} onClose={onClose} />
      ) : tab === 'shortlist' ? (
        <div className="space-y-2">
          {shortlist.length === 0 ? (
            <div className="text-sm font-medium text-app-sub italic px-0.5 py-4 text-center">Nothing saved in your Shortlist yet — add a new spot, or try Talisman Explorer.</div>
          ) : shortlist.map(a => (
            <div key={a.id}
              className="w-full p-3 rounded-xl bg-white border border-app-border/50 hover:border-gold/50 transition-colors flex items-center gap-3">
              {/* Name opens the saved item's portal view — read the full entry
                  before committing it to a time slot. The "+" places it. */}
              <button
                onClick={() => onViewSaved?.(a.id)}
                disabled={!onViewSaved}
                className="min-w-0 flex-1 text-left group/item"
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-sm font-black text-app-text leading-tight truncate">{a.name}</span>
                  {onViewSaved && <LinkIcon className="w-3 h-3 shrink-0 text-app-sub/30 group-hover/item:text-gold transition-colors" />}
                </div>
                {a.address && <div className="flex items-start gap-1 text-app-sub mt-0.5"><MapPin className="w-3 h-3 shrink-0 mt-0.5" /><span className="text-sm leading-snug truncate">{a.address}</span></div>}
              </button>
              <button aria-label="Add to this day"
                onClick={() => onAddDraft({ title: a.name, activityId: a.id, type: ACT_SLOT_TYPE[a.type] || 'experiences', ...(a.address ? { location: a.address } : {}) })}
                title="Add to this day"
                className="w-7 h-7 rounded-full bg-gold text-white flex items-center justify-center shrink-0 hover:brightness-110 active:scale-95 transition"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {/* Category picker — icon-only grid + selected label, matching the
              shortlist add/edit form's category picker instead of full-word
              pills (which forced horizontal scroll and truncated labels). */}
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 pr-1">
              <div className="ds-label">Category</div>
              {kind && <span className="ds-selection">{kind.label}</span>}
            </div>
            <div className="flex gap-1.5 bg-app-faint/30 p-1 rounded-xl border border-app-border/40 overflow-x-auto no-scrollbar">
              {SUGGEST_KINDS.map(k => {
                const IconComp = TAXONOMY.find(c => c.key === k.key)?.icon;
                return (
                  <button key={k.key} title={k.label} onClick={() => selectKind(k)}
                    className={`flex-1 min-w-[48px] flex items-center justify-center py-2.5 rounded-lg transition-all shrink-0 ${kind?.key === k.key ? 'bg-white shadow-sm ring-1 ring-app-border/10 scale-[1.02] text-gold' : 'text-app-sub hover:text-gold'}`}>
                    {IconComp && <IconComp className="w-4 h-4" />}
                  </button>
                );
              })}
            </div>
          </div>

          {!kind && <div className="text-sm font-medium text-app-sub italic px-0.5 py-4 text-center">Pick a category and Talisman Explorer will suggest places nearby.</div>}
          {loading && <div className="flex justify-center py-6"><div className="w-5 h-5 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>}
          {err && <div role="alert" className="text-sm font-medium text-rose-600 px-0.5">{err}</div>}

          {/* Carousel — mirrors the generated schedule's "Pick a spot" cards. */}
          {kind && !loading && !err && (
            (ideas[kind.key]?.length ? (
              // Bleed matches SpotModal's own px-6 so cards run to the edge.
              <div className="-mx-6 px-6">
                <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 pr-3 snap-x snap-mandatory">
                  {ideas[kind.key].map((rec, i) => {
                    const isAdded = added.has(rec.name + rec.address);
                    return (
                      <div key={i} className={`shrink-0 w-[88%] snap-center rounded-xl border transition-all ${isAdded ? 'border-emerald-400 ring-1 ring-emerald-400/40' : 'border-app-border/40'} bg-white p-4`}>
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="text-base font-black text-app-text font-serif italic leading-tight text-balance flex-1">{rec.name}</h4>
                          <button disabled={isAdded}
                            onClick={async () => { setAdded(s => new Set(s).add(rec.name + rec.address)); await onAddSuggestion(rec, kind); }}
                            className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-colors ${isAdded ? 'bg-emerald-500 text-white' : 'bg-gold text-white hover:brightness-110'}`}>
                            {isAdded ? <Check className="w-4 h-4 stroke-[3]" /> : <Plus className="w-4 h-4" />}
                          </button>
                        </div>
                        {rec.rating && rec.rating > 0 && <div className="mt-1.5"><StarRating rating={rec.rating} /></div>}
                        {rec.address && (
                          <div className="flex items-start gap-1.5 text-app-sub/60 mt-2">
                            <MapPin className="w-3 h-3 shrink-0 mt-0.5" />
                            <span className="text-[13px] font-normal leading-snug">{rec.address}</span>
                          </div>
                        )}
                        {rec.desc && <p className="text-xs text-app-sub leading-relaxed mt-2.5 line-clamp-3">{rec.desc}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-sm font-medium text-app-sub italic px-0.5 py-2">No ideas found — try another category.</div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Portal shell for the add-spot flow — a centred modal over a dimmed
// backdrop, matching the Trips/Logistics add-and-edit modals. Deliberately a
// plain conditional render with NO AnimatePresence wrapping the portal (that
// combination breaks mount/unmount in the iOS PWA — see the same note on
// Itinerary's EditModal).
function SpotModal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return <Modal title={title} onClose={onClose}>{children}</Modal>;
}

// Manual transport modes (used when a leg's endpoints can't be geocoded, so we
// can't auto-estimate). Icons mirror the derived-leg estimate icons.
const TRANSIT_MODES: { v: string; Icon: LucideIcon; label: string }[] = [
  { v: 'walking', Icon: Footprints, label: 'Walk' },
  { v: 'transit', Icon: TramFront,  label: 'Transit' },
  { v: 'driving', Icon: Car,        label: 'Drive' },
];

/** Compact inline editor for a manually-entered transport leg (mode + minutes),
 *  used between two places where at least one location isn't geocodable. */
function TransitEditor({ initial, onSave, onClear, onCancel }: {
  initial?: { mode: string; durationMin: number } | null;
  onSave: (v: { mode: string; durationMin: number }) => void;
  onClear: () => void; onCancel: () => void;
}) {
  const [mode, setMode] = useState(initial?.mode || 'walking');
  const [min, setMin] = useState<string>(initial?.durationMin ? String(initial.durationMin) : '');
  return (
    <div className="w-full bg-app-faint rounded-2xl border border-app-border/40 p-3 space-y-2.5">
      <div className="flex gap-1.5 justify-center">
        {TRANSIT_MODES.map(m => (
          <button key={m.v} onClick={() => setMode(m.v)}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-xl border text-[10px] font-black uppercase tracking-wider transition-all ${mode === m.v ? 'bg-gold text-white border-gold shadow-sm' : 'bg-white border-app-border/50 text-app-sub/60 hover:border-gold/40'}`}>
            <m.Icon className="w-3 h-3" /> {m.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input type="number" min={0} step={5} value={min} onChange={e => setMin(e.target.value)} placeholder="Minutes"
          className="flex-1 bg-white border border-app-border/40 rounded-xl px-3 py-2 text-sm font-bold text-app-text text-center focus:outline-none focus:ring-4 focus:ring-gold/5 focus:border-gold/40 transition placeholder:text-app-sub/40" />
        <span className="text-[10px] font-black uppercase tracking-widest text-app-sub/50">min</span>
      </div>
      <div className="flex gap-2">
        {initial && <button onClick={onClear} className="px-3 py-2 rounded-xl border border-rose-200 text-rose-500 text-[10px] font-black uppercase tracking-widest hover:bg-rose-50 transition">Clear</button>}
        <button onClick={onCancel} className="flex-1 py-2 rounded-xl border border-app-border text-app-sub text-[10px] font-black uppercase tracking-widest hover:bg-white transition">Cancel</button>
        <button onClick={() => { const n = parseInt(min, 10); if (!isNaN(n) && n > 0) onSave({ mode, durationMin: n }); }}
          className="flex-1 py-2 rounded-xl bg-gold text-white text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition">Save</button>
      </div>
    </div>
  );
}

/** Unified add/edit form for a schedule slot.
 *  - mode='edit' updates live via onChange (the list reflects edits immediately).
 *  - mode='add' collects a local draft and commits via onSubmit.
 *  Fields: Description (title) · Location (Places autocomplete → s.location) ·
 *  Start · Duration · Type · Notes · Lock. */
function SlotForm({ initial, mode, destName, linkedName, linkedLocation, onChange, onSubmit, onDelete, onViewItinerary, onClose }: {
  initial: any; mode: 'add' | 'edit'; destName: string;
  /** When this slot is linked to an itinerary item, its CURRENT name/location
   *  (read-only here — edited in the itinerary). */
  linkedName?: string; linkedLocation?: string;
  onChange?: (patch: any) => void;
  onSubmit?: (slot: any) => void;
  onDelete?: () => void;
  onViewItinerary?: (activityId?: string) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<any>(() => ({ type: 'experiences', durationMin: 90, start: '10:00', locked: false, ...initial }));
  const [confirmingUnlink, setConfirmingUnlink] = useState(false);
  // Type auto-suggest, matching the expense form: guess from what they type
  // until they pick one themselves, then never touch it again. Editing an
  // existing slot counts as already picked — its type was chosen once already.
  const [typeManuallySet, setTypeManuallySet] = useState(mode === 'edit');
  const [flashType, setFlashType] = useState<string | null>(null);
  const isTravel = draft.type === 'travel';
  const linked = !!draft.activityId;
  const set = (patch: any) => { setDraft((d: any) => ({ ...d, ...patch })); if (mode === 'edit') onChange?.(patch); };

  // Location field carries the Places autocomplete; selecting a place fills the
  // geocodable location (and the description if still blank).
  const { ref: locRef } = usePlacesWidget<HTMLInputElement>({
    onPlaceSelected: place => {
      const loc = place.formatted_address || place.name || '';
      const patch: any = { location: loc };
      if (!draft.title && place.name) patch.title = place.name;
      // Carry the Google rating over for a manually-added spot too, same as
      // ItemAddForm's Shortlist form — shown via the slot's own StarRating.
      const r = (place as any).rating;
      if (typeof r === 'number') patch.rating = r;
      set(patch);
      if (locRef.current) locRef.current.value = loc;
    },
    options: { fields: ['name', 'formatted_address', 'rating'], types: ['establishment', 'geocode'] },
  });

  const labelCls = 'ds-label block mb-1.5';
  // Matches the app's standard field metrics (px-4 py-3 + focus ring) rather
  // than the tighter px-3/py-2.5 this form used to run — that made these
  // inputs visibly shorter than every other form in the app. appearance-none
  // matters for the time/number inputs: without it iOS applies its own
  // control chrome and they end up a different height to their neighbours.
  const inputCls = 'w-full bg-white border border-app-border/60 rounded-xl px-4 py-3 text-sm font-bold text-app-text focus:outline-none focus:ring-4 focus:ring-gold/5 focus:border-gold/40 transition-all appearance-none placeholder:font-normal placeholder:text-app-sub/40';
  const roCls = 'w-full bg-app-faint/60 border border-app-border/40 rounded-xl px-4 py-3 text-sm font-bold text-app-sub/80';
  // Description + location read-only when linked (they live on the itinerary).
  const descVal = linked ? (linkedName ?? draft.title ?? '') : draft.title;
  const locVal = linked ? (linkedLocation ?? draft.location ?? '') : draft.location;

  return (
    <div className="space-y-4">
      {/* Linked-to-itinerary row — view the source, or unlink to edit freely. */}
      {linked && (
        confirmingUnlink ? (
          <div className="p-3 rounded-xl border border-app-border/50 bg-app-faint/40 space-y-2.5">
            <div className="text-sm font-black text-app-text">Unlink from this saved item?</div>
            <div className="text-sm font-medium text-app-sub leading-snug">Changes made to it in your Shortlist will no longer sync to your plan.</div>
            <div className="flex gap-2">
              <button onClick={() => setConfirmingUnlink(false)} className="flex-1 py-2 rounded-xl border border-app-border text-app-sub text-[10px] font-black uppercase tracking-widest hover:bg-white transition">Cancel</button>
              <button onClick={() => { set({ activityId: null, title: descVal, ...(locVal ? { location: locVal } : {}) }); setConfirmingUnlink(false); }} className="flex-1 py-2 rounded-xl bg-rose-500 text-white text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition">Unlink</button>
            </div>
          </div>
        ) : (
          <div className="flex items-stretch gap-1 rounded-xl border border-gold/30 bg-gold/5">
            {/* Tapping the row opens the saved item's own portal view — no
                separate "View saved item" link needed. Unlink stays a
                distinct, explicit action beside it. */}
            <button
              onClick={() => onViewItinerary?.(draft.activityId)}
              disabled={!onViewItinerary}
              className="flex-1 min-w-0 flex items-center gap-2 px-3 py-2.5 text-left rounded-l-xl hover:bg-gold/10 transition-colors disabled:cursor-default"
            >
              <LinkIcon className="w-3.5 h-3.5 text-gold shrink-0" />
              <span className="text-[10px] font-black uppercase tracking-widest text-app-sub/50 truncate">Linked item</span>
              {onViewItinerary && <ChevronRight className="w-3.5 h-3.5 text-gold/50 shrink-0 ml-auto" />}
            </button>
            <button onClick={() => setConfirmingUnlink(true)} className="px-3 text-[10px] font-black uppercase tracking-widest text-app-sub/60 hover:text-rose-500 transition-colors shrink-0">Unlink</button>
          </div>
        )
      )}

      {/* This writes the slot's `title` — "Description" read as a synonym for
          the Notes field below it. "What" is unambiguous next to Location /
          Start time / Type / Notes. */}
      <div>
        <label className={labelCls}>What</label>
        {linked
          ? <div className={roCls}>{descVal || '—'}</div>
          : <input type="text" value={descVal || ''} onChange={e => {
              const title = e.target.value;
              const patch: any = { title };
              if (!typeManuallySet) {
                const key = suggestL1Key(title);
                // Only the types this form actually offers — transport and
                // accommodation are not schedule activities.
                if (key && key !== draft.type && TYPE_CHOICES.some(t => t.v === key)) {
                  patch.type = key;
                  setFlashType(key);
                  window.setTimeout(() => setFlashType(prev => prev === key ? null : prev), 650);
                }
              }
              set(patch);
            }} placeholder="e.g. Explore historical city centre" className={inputCls} />}
      </div>

      {!isTravel && (
        <div>
          <label className={labelCls}>Location</label>
          {linked
            ? <div className={roCls}>{locVal || '—'}</div>
            : <input ref={locRef} type="text" defaultValue={draft.location || ''} onChange={e => set({ location: e.target.value })} placeholder={`Search in ${destName}…`} className={inputCls} />}
        </div>
      )}

      {/* Each column is its own flex-col so the lock button can flex-1 to the
          exact height of the inputs beside it — matching their padding alone
          left it short, since an icon's line box is smaller than text's. */}
      <div>
        <div className="flex gap-3 items-stretch">
          <div className="flex-1 flex flex-col">
            <label className={labelCls}>Start time</label>
            <input type="time" value={draft.start} onChange={e => set({ start: e.target.value })} className={inputCls} />
          </div>
          <div className="flex-1 flex flex-col">
            <label className={labelCls}>Duration (min)</label>
            {/* Always snap to 5-min granularity so timestamps stay clean. */}
            <input type="number" min={5} step={5} value={draft.durationMin || ''}
              onChange={e => set({ durationMin: Math.max(0, parseInt(e.target.value) || 0) })}
              onBlur={() => { if (draft.durationMin > 0) set({ durationMin: roundUp5(draft.durationMin) }); }}
              placeholder="90" className={inputCls} />
          </div>
          {/* Lock/unlock, folded into the timing row as a compact toggle rather
              than its own full-width button below — it's a secondary setting
              on this slot's time, not a section of its own. */}
          <div className="flex flex-col">
            <label className={`${labelCls} invisible`}>Lock</label>
            <button
              onClick={() => set({ locked: !draft.locked })}
              className={`flex-1 px-4 rounded-xl border transition-all flex items-center justify-center ${draft.locked ? 'bg-gold/5 border-gold/40 text-gold' : 'bg-white border-app-border/60 text-app-sub/60 hover:border-gold/30'}`}
            >
              {draft.locked ? <Lock className="w-4 h-4" /> : <LockOpen className="w-4 h-4" />}
            </button>
          </div>
        </div>
        {/* Says what the CURRENT lock state means, and flips with the toggle —
            the icon alone doesn't tell you which way round it is. */}
        <p className="text-sm font-medium text-app-sub leading-snug mt-1.5 px-0.5">
          {draft.locked
            ? 'Pinned to this time — it stays put when earlier spots change.'
            : 'Shifts automatically when earlier spots change length or move.'}
        </p>
      </div>

      {!isTravel && (
        <div>
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 pr-1 mb-1.5">
            <label className={labelCls.replace(' mb-1.5', '')}>Type</label>
            <span className="ds-selection">
              {TYPE_CHOICES.find(t => t.v === normalizeSlotType(draft.type))?.label}
            </span>
          </div>
          <CategoryPicker
            options={TYPE_CHOICES.map(({ v, label }) => ({ key: v, label, icon: SLOT_ICON[v] || L1_BY_KEY.other.icon }))}
            value={normalizeSlotType(draft.type)}
            flash={flashType}
            onChange={v => { set({ type: v }); setTypeManuallySet(true); setFlashType(null); }}
          />
        </div>
      )}

      <div>
        <label className={labelCls}>Notes</label>
        <textarea value={draft.note || ''} onChange={e => set({ note: e.target.value })} rows={2}
          placeholder="Anything to remember…" className={`${inputCls} resize-none font-medium`} />
      </div>

      <div className="flex gap-2 pt-1">
        {mode === 'edit' ? (
          <>
            <button aria-label="Delete" onClick={onDelete} className="px-4 py-2.5 rounded-xl border border-rose-200 text-rose-500 hover:bg-rose-50 transition flex items-center justify-center gap-1.5">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-gold text-white text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition">Done</button>
          </>
        ) : (
          <>
            <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-app-border text-app-sub text-[10px] font-black uppercase tracking-widest hover:bg-white transition">Cancel</button>
            <button disabled={!(draft.title || '').trim()} onClick={() => onSubmit?.(draft)}
              className="flex-1 py-2.5 rounded-xl bg-gold text-white text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition disabled:opacity-50">Add to plan</button>
          </>
        )}
      </div>
    </div>
  );
}

function ScheduleView({ logisticsGaps, missingAccommodation, hasGenerated, schedule, destName, headerTints, accommodation, coordCache, availableActs, travellers = [], transports, fillingDay, activeDate, dayBadge, suggestPrefs, transportPref, onAutoFillTrip, onFillDay, onChange, onOpenPreferences, onExitToItinerary, onUncommitPending }: {
  schedule: any; destName: string;
  /** 1 or 2 accent hexes — see HeaderTint above. */
  headerTints: string[];
  accommodation?: { name?: string; address?: string }; coordCache: Record<string, { lat: number; lng: number }>;
  logisticsGaps: { transport: ScheduleGap; accommodation: ScheduleGap };
  missingAccommodation: boolean;
  hasGenerated: boolean;
  availableActs: any[]; fillingDay: number | null; activeDate: string; dayBadge?: string; suggestPrefs: string;
  travellers?: { id: string; name: string }[];
  transports: Transport[];
  /** Public-vs-private preference, applied per leg when routing. */
  transportPref: TransportPref;
  onAutoFillTrip: () => void; onFillDay: (di: number) => void; onChange: (s: any) => void;
  /** Opens the 5-step planning-preferences wizard (pace, interests, budget…). */
  onOpenPreferences?: () => void;
  onExitToItinerary?: (activityId?: string) => void;
  /** Deselects a committed item from the Pending Shortlist review modal. */
  onUncommitPending: (item: any) => Promise<void>;
}) {
  const days: any[] = (schedule?.days || []).map((day: any) => projectScheduleDay(day, transports));
  const [editing, setEditing] = useState<string | null>(null);  // slot id being edited
  const [adding, setAdding] = useState<number | null>(null);     // day index whose add-panel is open
  const [addSeed, setAddSeed] = useState<{ start?: string; tab?: 'ideas' } | null>(null);
  const [expandedLeg, setExpandedLeg] = useState<string | null>(null);  // "date-si" of the open leg map
  // Leg whose "open these directions in a maps app" chooser is showing.
  const [directionsFor, setDirectionsFor] = useState<{ a: { lat: number; lng: number }; b: { lat: number; lng: number }; mode: LegMode } | null>(null);
  // Confirmation for anything that calls onAutoFillTrip on a plan that
  // already has content — a portal popup (not window.confirm) with copy
  // specific to what triggered it, since "refresh to fit new items" and
  // "start over" carry different weight.
  const [confirmRefresh, setConfirmRefresh] = useState<{ title: string; message: string } | null>(null);
  const [showPending, setShowPending] = useState(false);  // Pending Shortlist items review modal
  const [expandedSlot, setExpandedSlot] = useState<string | null>(null);  // slot id of expanded card
  /** Set when a traveller taps the reorder arrows on a slot that can't move. */
  const [pinnedNotice, setPinnedNotice] = useState<{ journey: boolean } | null>(null);
  const [editingTransit, setEditingTransit] = useState<string | null>(null);  // slot id whose transit is being edited
  const [addingTransit, setAddingTransit] = useState<string | null>(null);  // slot id whose add-transport panel is open
  const [undo, setUndo] = useState<{ prev: any; label: string } | null>(null);
  const undoTimer = useRef<any>(null);
  // Snapshot the slot's end-time when an edit opens so we can cascade-shift
  // subsequent unlocked slots when the editor closes.
  const editSnapshot = useRef<{ di: number; id: string; start: string; durationMin: number } | null>(null);
  // Coords resolved on the fly (keyed by the geocodable query string) for slots
  // that aren't linked to a pre-geocoded itinerary activity — e.g. AI option
  // slots and free-text titles. Lets transport legs show a real time + mode
  // estimate instead of a bare "View route".
  const [legCoords, setLegCoords] = useState<Record<string, { lat: number; lng: number }>>({});
  // Real routed leg durations + chosen mode, keyed by the visual leg key
  // (`${date}-${si}:${route identity}`). Filled async by fetchLegRoute;
  // until then the leg chip falls back to legEstimate's straight-line guess.
  const [legRoutes, setLegRoutes] = useState<Record<string, LegRoute>>({});
  // Slots currently fetching Gemini suggestions (description-only entries with no
  // saved location). suggestTried guards against re-fetching the same slot.
  const [suggestLoading, setSuggestLoading] = useState<Set<string>>(new Set());
  const suggestTried = useRef<Set<string>>(new Set());

  const reSort = (slots: any[]) => [...slots].sort(compareScheduleTime);

  const openSlotEdit = (di: number, id: string | null) => {
    if (id) {
      const s = (days[di]?.slots || []).find((x: any) => x.id === id);
      editSnapshot.current = s ? { di, id, start: s.start, durationMin: s.durationMin || 0 } : null;
    }
    setEditing(id);
  };

  const closeSlotEdit = (di: number, id: string) => {
    const snap = editSnapshot.current;
    if (snap && snap.di === di && snap.id === id) {
      const daySlots: any[] = days[di]?.slots || [];
      const s = daySlots.find((x: any) => x.id === id);
      // Only recompute when this item's start or duration actually changed, so an
      // edit that touches neither doesn't collapse the rest of the day.
      if (s && (s.start !== snap.start || (s.durationMin || 0) !== snap.durationMin)) {
        const newSlots = cascadeTimes(daySlots, id, legMin);
        onChange({ ...schedule, days: days.map((d: any, i: number) => i !== di ? d : { ...d, slots: newSlots }) });
      }
    }
    editSnapshot.current = null;
    setEditing(null);
  };

  // Apply a change with a 6-second undo affordance (delete, optimise, …).
  const applyWithUndo = (next: any, label: string) => {
    if (undoTimer.current) clearTimeout(undoTimer.current);
    setUndo({ prev: schedule, label });
    onChange(next);
    undoTimer.current = setTimeout(() => setUndo(null), 6000);
  };
  const doUndo = () => { if (undo) { onChange(undo.prev); setUndo(null); if (undoTimer.current) clearTimeout(undoTimer.current); } };
  useEffect(() => () => { if (undoTimer.current) clearTimeout(undoTimer.current); }, []);

  const updateSlot = (di: number, id: string, patch: any) => {
    onChange({ ...schedule, days: days.map((d, i) => i !== di ? d : { ...d, slots: reSort(d.slots.map((s: any) => s.id !== id ? s : { ...s, ...patch })) }) });
  };

  // Travel minutes between two slots, used to keep the cascade watertight
  // (0 when either end has no resolved location — e.g. free time).
  const legMin = (a: any, b: any): number => {
    if (a.legAfter) return a.legAfter.durationMin || 0;
    const ca = slotCoords(a), cb = slotCoords(b);
    if (!ca || !cb) return a.transit?.durationMin || 0;
    const estimate = legEstimate(ca, cb, GEMINI_MODE[a.travelMode]).durationMin;
    if (estimate > MAX_AUTO_LEG_MIN) return a.transit?.durationMin || 0;
    const day = days.find(d => d.slots.some((s: any) => s.id === a.id));
    const index = day ? reSort(day.slots).findIndex((s: any) => s.id === a.id) : -1;
    const route = legRoutes[`${day?.date}-${index}:${legRouteKey(ca, cb, GEMINI_MODE[a.travelMode], transportPref)}`];
    return round5(route?.durationMin ?? estimate);
  };

  // insertSlot adds without closing the add-panel (so Ideas can add several);
  // addSlot wraps it and closes the panel (for one-shot picks). Adding cascades
  // from the new slot so later unlocked items get pushed out.
  //
  // A manual add is also the one place optimizeDayOrder runs automatically —
  // quietly, no button, no undo toast, folded into the same onChange as the
  // insert itself. This is deliberately NOT surfaced as a visible "Optimise"
  // control: a generated day is already sequenced geographically by the
  // Gemini prompt (travelServices.ts), so the only time route order actually
  // needs tidying is right after a traveller drops a new spot into an
  // existing plan without thinking about where it sits relative to
  // everything else — exactly this moment.
  const insertSlot = (di: number, slot: any, openEdit = false) => {
    const newSlot = { id: uid(), durationMin: 90, type: 'experiences', start: '10:00', ...slot };
    const lastClock = days[di].slots.filter((s: any) => s.clock).at(-1)?.clock;
    if (lastClock && !newSlot.clock) newSlot.clock = lastClock;
    const merged = reSort([...(days[di].slots || []), newSlot]);
    const cascaded = cascadeTimes(merged, newSlot.id, legMin);
    const tidied = optimizeDayOrder(cascaded, slotCoords, coordCache['__accom']);
    onChange({ ...schedule, days: days.map((d, i) => i !== di ? d : { ...d, slots: tidied }) });
    if (openEdit) setEditing(newSlot.id);
    return newSlot.id;
  };
  const addSlot = (di: number, slot: any, openEdit = false) => {
    insertSlot(di, slot, openEdit);
    setAdding(null); setAddSeed(null);
  };
  const deleteSlot = (di: number, id: string) => {
    editSnapshot.current = null;
    setEditing(null);
    const daySlots: any[] = days[di]?.slots || [];
    const ordered = reSort(daySlots);
    const pos = ordered.findIndex((x: any) => x.id === id);
    // Re-chain from the item before the deleted one so the gap closes; deleting
    // the first item leaves the rest where they are.
    const anchorId = pos > 0 ? ordered[pos - 1].id : null;
    const remaining = daySlots.filter((s: any) => s.id !== id);
    const cascaded = anchorId ? cascadeTimes(remaining, anchorId, legMin) : remaining;
    applyWithUndo({ ...schedule, days: days.map((d, i) => i !== di ? d : { ...d, slots: cascaded }) }, 'Spot removed');
  };

  /**
   * Move a slot one place earlier or later among its MOVABLE neighbours.
   *
   * This used to swap the two slots' START TIMES and leave their durations
   * where they were, which only works when the two happen to be the same
   * length. A two-hour gallery visit at 10:00 trading times with a
   * half-hour coffee at 12:00 came back as 12:00–14:00 — straight over
   * whatever sat at 12:30. That's the overlap.
   *
   * So it swaps POSITIONS and re-lays the day from the earlier of the two:
   * that position keeps the time the block already started at, and
   * everything after it is recomputed from each item's own duration plus the
   * travel leg before it. A longer item moving ahead of a shorter one now
   * pushes the rest of the day down instead of landing on top of it.
   *
   * Locked slots (including booked journeys) are skipped rather than swapped;
   * assumed journeys without known start times remain movable. Locks survive the
   * re-lay: relayFrom treats them as fixed anchors and flows the unlocked
   * items around them, so stepping a museum visit past a booked flight moves
   * the museum and leaves the flight exactly where it is.
   */
  const moveSlot = (di: number, id: string, dir: 'up' | 'down') => {
    const ordered = reSort(days[di].slots);
    const pos = ordered.findIndex(s => s.id === id);
    if (pos < 0 || ordered[pos].locked) return;
    const step = dir === 'up' ? -1 : 1;
    let j = pos + step;
    while (j >= 0 && j < ordered.length && ordered[j].locked) j += step;
    // At an edge there is no movable item to trade with; cross the adjacent
    // fixed booking instead. Its timestamp stays untouched.
    if (j < 0 || j >= ordered.length) j = pos + step;
    if (j < 0 || j >= ordered.length) return;
    if (ordered[pos].clock?.zone !== ordered[j].clock?.zone) return;

    if (ordered[j].locked) {
      const moving = ordered[pos], fixed = ordered[j];
      const minute = step < 0
        ? scheduleMinute(fixed) - (moving.durationMin || 0) - legMin(moving, fixed)
        : scheduleMinute(fixed) + (fixed.durationMin || 0) + legMin(fixed, moving);
      const next = ordered.filter(s => s.id !== id);
      next.splice(j, 0, shiftScheduleTime(moving, minute));
      onChange({ ...schedule, days: days.map((d, i) => i !== di ? d : { ...d, slots: next }) });
      return;
    }

    const swapped = [...ordered];
    swapped[pos] = ordered[j];
    swapped[j] = ordered[pos];

    // Both ends of the swap are movable by construction, so whichever now
    // sits at the earlier index is safe to pin the block's original start on.
    // Keeping that start is what stops a reorder quietly dragging the whole
    // day earlier — the traveller asked for two things to trade places, not
    // for everything to shuffle up.
    //
    // Unless the newly-adjacent pair needs longer to travel between than the
    // gap that was there: the leg before this slot is now a different leg,
    // and a slower one has to push the block later or it starts before you
    // could possibly arrive.
    const first = Math.min(pos, j);
    const before = first > 0 ? swapped[first - 1] : null;
    const earliest = before
      ? scheduleMinute(before) + (before.durationMin || 0) + legMin(before, swapped[first])
      : -Infinity;
    swapped[first] = shiftScheduleTime(swapped[first], Math.max(scheduleMinute(ordered[first]), earliest));
    const next = relayFrom(swapped, first, legMin);
    onChange({ ...schedule, days: days.map((d, i) => i !== di ? d : { ...d, slots: next }) });
  };
  /** Whether `moveSlot(di, id, dir)` would find a movable neighbour to swap
   *  with — drives whether the reorder button is shown at all. */
  const canMoveSlot = (di: number, id: string, dir: 'up' | 'down'): boolean => {
    const slots = reSort(days[di].slots);
    const pos = slots.findIndex(s => s.id === id);
    if (pos < 0 || slots[pos].locked) return false;
    const step = dir === 'up' ? -1 : 1;
    let j = pos + step;
    while (j >= 0 && j < slots.length && slots[j].locked) j += step;
    if (j < 0 || j >= slots.length) j = pos + step;
    return j >= 0 && j < slots.length && slots[pos].clock?.zone === slots[j].clock?.zone;
  };

  // Activities the traveller planned/shortlisted but hasn't placed on a day yet.
  const placedIds = new Set<string>();
  days.forEach(d => (d.slots || []).forEach((s: any) => { if (s.activityId) placedIds.add(s.activityId); }));
  // "Pending" = in-plan items not yet on any day. A shortlist item the
  // traveller placed via "Pick a spot" is NOT pending (it's placed) and does
  // NOT count here even though its source stays inPlan:false — being on the
  // schedule is what matters, not the flag.
  // Transports are always inPlan:true and never match placedIds (they're
  // rendered automatically as connector legs between slots, never picked
  // onto one via `activityId`) — without excluding them here, every flight/
  // train permanently counts as "pending" and shows up (badly, since it has
  // no addedBy/notes/category in the shortlist-item shape) in the Pending
  // Shortlist Items review modal and the "From Shortlist" add-a-spot list.
  const unplaced  = availableActs.filter(a => a.inPlan && a.type !== 'transport' && !placedIds.has(a.id));
  const shortlist = availableActs.filter(a => !a.inPlan && !placedIds.has(a.id));

  // A geocodable point for a slot's location, used to build map routes. CRUCIAL:
  // every point we return MUST resolve, because one bad waypoint makes the whole
  // Google Directions embed fall back to a zoomed-out world map.
  //  - generic slots (options) → the chosen/first option's street address (always present)
  //  - activity-linked slots → the precisely-resolved coords; if we couldn't
  //    geocode it (e.g. a personal event like "Party with Esteban"), return null
  //    so it's skipped rather than breaking the route
  //  - other specific named places → name + city
  //  - free slots have no location of their own
  //  - travel slots are normally skipped, but an arrival/departure annotation
  //    carries a geocodable location (airport/station/lodging) so its leg works
  const slotQuery = (s: any): string | null => {
    if (!s || s.type === 'free') return null;
    if (s.type === 'travel') return (s.location && s.location.trim()) ? s.location.trim() : null;
    // A picked activityId (whether a pre-existing Shortlist item or one Pick-
    // a-spot just created) is the resolved location now — check it BEFORE
    // falling back to options[0], so transport legs re-anchor to whichever
    // option was actually selected instead of always the first candidate.
    if (s.activityId) {
      const c = coordCache[s.activityId];
      if (c) return `${c.lat},${c.lng}`;
      const picked = s.options?.find((o: any) => o.shortlistId === s.activityId);
      if (picked) return picked.location?.trim() || picked.address || `${picked.title}, ${destName}`;
      // No cached coords and no matching option — fall through to location/title below.
    } else if (s.options?.length) {
      const idx = (typeof s.chosen === 'number' && s.chosen >= 0 && s.options[s.chosen]) ? s.chosen : 0;
      const o = s.options[idx];
      // Prefer the precise geocodable location; fall back to the shown address,
      // then the title. Selecting a different option re-queries this.
      return o.location?.trim() || o.address || `${o.title}, ${destName}`;
    }
    // Gemini supplies a precise, geocodable "location" for every placed slot —
    // prefer it over the casual title (e.g. title "Explore Le Marais" →
    // location "Place des Vosges, Paris"), so the route preview and the leg
    // estimate resolve to a real point.
    if (s.location && s.location.trim()) return s.location.trim();
    // Fallback for older schedules / custom slots with no location: the title.
    const name = (s.title || '').trim();
    if (!name) return null;
    return `${name}, ${destName}`;
  };

  const slotCoords = (s: any): { lat: number; lng: number } | undefined => {
    if (s?.activityId && coordCache[s.activityId]) return coordCache[s.activityId];
    const q = slotQuery(s);
    if (!q) return undefined;
    const m = q.match(/^(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)$/);
    if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
    return legCoords[q];
  };

  // Geocode any text-query slots in the active day that we don't have coords for
  // yet, so transport legs show a real time + mode estimate. Runs once per unique
  // query (results cached in legCoords). Placed before the early return so hook
  // order stays stable.
  const dayForGeocode = days.find(d => d.date === (activeDate || days[0]?.date)) || days[0];
  const pendingQueries = (dayForGeocode?.slots || [])
    .map((s: any) => slotQuery(s))
    .filter((q: string | null): q is string => !!q && !/^-?\d+(?:\.\d+)?,-?\d+(?:\.\d+)?$/.test(q) && !legCoords[q]);
  const pendingKey = pendingQueries.join('|');
  useEffect(() => {
    if (!pendingQueries.length) return;
    let cancelled = false;
    (async () => {
      await loadGoogleMaps();
      const g = (window as any).google;
      if (!g?.maps?.places) return;
      const svc = new g.maps.places.PlacesService(document.createElement('div'));
      const find = (query: string) => new Promise<{ lat: number; lng: number } | null>(res => {
        svc.findPlaceFromQuery({ query, fields: ['geometry'] }, (r: any, st: any) => {
          const loc = st === g.maps.places.PlacesServiceStatus.OK && r?.[0]?.geometry?.location;
          res(loc ? { lat: loc.lat(), lng: loc.lng() } : null);
        });
      });
      const out: Record<string, { lat: number; lng: number }> = {};
      for (const q of pendingQueries) { const c = await find(q); if (c) out[q] = c; }
      if (!cancelled && Object.keys(out).length) setLegCoords(prev => ({ ...prev, ...out }));
    })();
    return () => { cancelled = true; };
  }, [pendingKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ask Google for the real routed duration + sensible mode of each leg in the
  // active day, once both ends have coords. Replaces the straight-line guess
  // shown in the chip and used for "leave by". Identity includes endpoints and preference.
  const routeLegs = (() => {
    const day = dayForGeocode;
    if (!day) return [] as { key: string; a: { lat: number; lng: number }; b: { lat: number; lng: number }; prefer?: LegMode; when?: Date }[];
    const ordered = reSort(day.slots || []);
    const out: { key: string; a: { lat: number; lng: number }; b: { lat: number; lng: number }; prefer?: LegMode; when?: Date }[] = [];
    for (let i = 0; i < ordered.length - 1; i++) {
      const s = ordered[i], nxt = ordered[i + 1];
      // The renderer also shows connectors into and out of geocoded travel
      // annotations (for example, an activity → airport departure). Only a
      // booked journey legAfter bypasses that connector.
      if (s.legAfter) continue;
      const a = slotCoords(s), b = slotCoords(nxt);
      if (!a || !b) continue;
      // Match the elapsed-time order used by the renderer and legMin.
      const si = i;
      const [h, m] = (s.start || '09:00').split(':').map(Number);
      const when = new Date(day.date + 'T00:00');
      when.setHours(h || 9, m || 0);
      out.push({ key: `${day.date}-${si}:${legRouteKey(a, b, GEMINI_MODE[s.travelMode], transportPref)}`, a, b, prefer: GEMINI_MODE[s.travelMode], when });
    }
    return out;
  })();
  const routeLegsKey = routeLegs.map(l => l.key).join('|');
  useEffect(() => {
    if (!routeLegs.length) return;
    let cancelled = false;
    (async () => {
      for (const leg of routeLegs) {
        if (legRoutes[leg.key]) continue;
        const r = await fetchLegRoute(leg.a, leg.b, leg.prefer, leg.when, transportPref);
        if (!cancelled && r) setLegRoutes(prev => ({ ...prev, [leg.key]: r }));
      }
    })();
    return () => { cancelled = true; };
  }, [routeLegsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // For open-ended entries, including wellness slots the model sometimes
  // gives a vague city location and marks as free time,
  // fetch concrete Gemini suggestions and attach them as the slot's options so a
  // "Pick a spot" carousel appears — just like a generated generic slot. Fetched
  // once per slot (suggestTried) and applied in a single batched write.
  const needSuggest = (dayForGeocode?.slots || []).filter((s: any) =>
    !s.options?.length && !s.activityId && (s.title || '').trim()
    && needsSpotSuggestions(s, destName)
    && (s.type !== 'free' || WELLNESS_PLACE_RE.test(s.title))
    && s.type !== 'travel' && !suggestTried.current.has(s.id));
  const needSuggestKey = needSuggest.map((s: any) => s.id).join('|');
  useEffect(() => {
    if (!needSuggest.length || !dayForGeocode) return;
    const dIdx = days.findIndex(dd => dd.date === dayForGeocode.date);
    if (dIdx < 0) return;
    let cancelled = false;
    const claimed: string[] = needSuggest.map((s: any) => s.id);
    // Slots whose fetch actually finished, successfully or not. Anything left
    // in `claimed` when this effect is torn down never ran, so the cleanup
    // below can tell "done" from "abandoned".
    const settled = new Set<string>();
    needSuggest.forEach((s: any) => suggestTried.current.add(s.id));
    setSuggestLoading(prev => { const n = new Set(prev); claimed.forEach(id => n.add(id)); return n; });
    (async () => {
      const results: { id: string; options: any[] }[] = [];
      for (const s of needSuggest) {
        try {
          const recs = await getRecommendations(destName, recTypeForSlot(s.type, s.title),
            WELLNESS_PLACE_RE.test(s.title)
              ? `Concrete spas, bathhouses or massage studios for "${s.title}"; no nightclubs or unrelated events.`
              : `Specific places matching: "${s.title}"`);
          // Same check the Explorer carousel applies: these are places a model
          // wrote from memory, with an estimated rating presented as Google's,
          // and they get SAVED into the schedule from here. A suggestion Places
          // can't confirm is dropped rather than offered; null means Places
          // itself was unavailable, which is no verdict, so they stand.
          const confirmed = recs?.length ? await confirmPlaces(recs) : null;
          const usable = (recs || []).flatMap((r: any) => {
            if (confirmed === null) return [{ ...r, rating: undefined }];
            const facts = confirmed.get(String(r.name || '').trim());
            return facts ? [{ ...r, rating: undefined, ...facts }] : [];
          });
          if (usable.length) results.push({ id: s.id, options: usable.slice(0, 5).map((r: any) => ({ title: r.name, desc: r.desc, address: r.address, location: r.address, ...(r.rating ? { rating: r.rating } : {}) })) });
        } catch { /* leave as-is; suggestTried prevents a retry loop */ }
        settled.add(s.id);
        if (!cancelled) setSuggestLoading(prev => { const n = new Set(prev); n.delete(s.id); return n; });
      }
      if (cancelled || !results.length) return;
      onChange({ ...schedule, days: days.map((dd: any, i: number) => i !== dIdx ? dd : { ...dd, slots: reSort((dd.slots || []).map((x: any) => {
        const hit = results.find(r => r.id === x.id);
        return hit ? { ...x, options: hit.options, chosen: -1 } : x;
      })) }) });
    })();
    return () => {
      cancelled = true;
      // Whatever hadn't finished when this effect was torn down — switching
      // day mid-fetch is the usual cause — must not be left mid-flight.
      // Previously the per-slot `setSuggestLoading` delete was skipped once
      // cancelled, so those ids stayed in the Set for the life of the
      // component and their cards span "Finding spots…" forever, while
      // suggestTried simultaneously guaranteed they'd never be retried.
      const abandoned = claimed.filter(id => !settled.has(id));
      if (!abandoned.length) return;
      abandoned.forEach(id => suggestTried.current.delete(id));
      setSuggestLoading(prev => {
        const n = new Set(prev);
        abandoned.forEach(id => n.delete(id));
        return n;
      });
    };
  }, [needSuggestKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeDi = Math.max(0, days.findIndex(d => d.date === (activeDate || days[0]?.date)));
  const activeDay = days[activeDi];

  if (!activeDay) return <div className="space-y-3" />;

  const di = activeDi;
  const d = activeDay;

  // A geocodable point for a slot's location: prefer the pre-geocoded itinerary
  // activity coords; else parse a "lat,lng" query; else use coords resolved on
  // the fly for this slot's text query (see the geocoding effect below).
  // Transport connectors render BETWEEN consecutive slots in time order — every
  // step gets a leg to the NEXT step in the full sequence, so the day reads
  // item → transport → item throughout (incl. across the arrival journey:
  // origin activity → station → Depart → booked leg → Arrive → transfer →
  // Check-in → first activity). Where both ends are geocodable we auto-show a
  // transit estimate + route map; otherwise the user adds it manually.
  const orderedAll = reSort(d.slots || []);
  const nextSlotOf: Record<string, any> = {};
  for (let i = 0; i < orderedAll.length - 1; i++) nextSlotOf[orderedAll[i].id] = orderedAll[i + 1];

  // Time-overlap detection: where one item's [start, start+duration) runs past
  // the next item's start (normally only possible when a locked item anchors an
  // earlier time than the chain allows). BOTH items are tagged and their
  // timestamps shown in red.
  const overlapIds = new Set<string>();
  const timeOrder = reSort((d.slots || []).filter((s: any) => !(s.type === 'travel')));
  for (let i = 0; i < timeOrder.length - 1; i++) {
    const cur = timeOrder[i], nxt = timeOrder[i + 1];
    if (scheduleMinute(cur) + (cur.durationMin || 0) > scheduleMinute(nxt)) { overlapIds.add(cur.id); overlapIds.add(nxt.id); }
  }

  // Every derived clock reading in the day sits on the 5-minute grid (rounded
  // up). Leg *durations* stay exact — only the timestamp is snapped.
  const stampAt = (min: number) => formatClock(toHHMM(round5(Math.max(0, min))));

  const openAdd = (start?: string, tab?: 'ideas') => {
    setAdding(di); setAddSeed(start || tab ? { start, tab } : null);
  };
  const closeAdd = () => { setAdding(null); setAddSeed(null); };

  // The add flow is a portal modal, so it opens over the day wherever you
  // tapped "+" — the seeded start time still decides where the spot lands.
  const addSpotModal = adding === di ? (
    <SpotModal title="Add a spot" onClose={closeAdd}>
      <AddSpotPanel
        shortlist={[...unplaced, ...shortlist]} destName={destName} suggestPrefs={suggestPrefs}
        seedStart={addSeed?.start} initialTab={addSeed?.tab}
        onAddDraft={draft => addSlot(di, { ...draft, ...(addSeed?.start && !draft.start ? { start: addSeed.start } : {}) })}
        onAddSuggestion={async (rec, kind) => {
          insertSlot(di, { title: rec.name, type: kind.key, durationMin: 90, ...(rec.rating ? { rating: rec.rating } : {}), ...(rec.address ? { location: rec.address } : {}), ...(addSeed?.start ? { start: addSeed.start } : {}) });
        }}
        onViewSaved={onExitToItinerary}
        onClose={closeAdd}
      />
    </SpotModal>
  ) : null;

  /**
   * Whether a day holds anything the traveller actually planned.
   *
   * Not simply "has slots". Day one of a trip always has something on it —
   * the arrival, either as a real journey leg derived from the booked
   * transport or as the estimated travel allowance that stands in for one —
   * and neither is a plan. Counting them meant the very first day of every
   * trip, the day most likely to be opened first, was the one day that never
   * offered to fill itself.
   */
  const plannedSlots = (slots: any[] | undefined) =>
    (slots || []).filter(slot => !slot?.estimatedTravel && slot?.type !== 'travel');

  const allEmpty = days.every(dd => plannedSlots(dd.slots).length === 0);
  // The currently-viewed day specifically — distinct from allEmpty (whole
  // trip), which still gates the plan-level actions below (Preferences /
  // Refresh Plan only make sense once a plan exists somewhere).
  const dayEmpty = plannedSlots(d.slots).length === 0;
  const assumedSlots = (d.slots || []).filter((slot: any) => slot.estimatedTravel || slot.placeholder);
  /**
   * What this destination hasn't got yet, in the order the plan needs it.
   * The generator works from exactly these three, so naming the ones that
   * are absent is more use than repeating the full list of what to add.
   */
  const gapHeadline = scheduleGapHeadline({
    ...logisticsGaps,
    // "shortlist items saved" rather than "saved spots": the tab is called
    // Shortlist, so name the thing the traveller has to go and do.
    shortlist: !availableActs.some((a: any) => a.type !== 'transport'),
  });
  const itineraryGapsToOpen = !!(logisticsGaps.transport || logisticsGaps.accommodation);

  return (
    <div className="space-y-3">
      {/* Why the arrows on this slot do nothing. It's a portal so it clears
          the day's own scrolling column, and it says the one thing that
          actually unblocks the traveller rather than just apologising. */}
      {pinnedNotice && createPortal(
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          onClick={() => setPinnedNotice(null)}
          className="fixed inset-0 z-[220] flex items-center justify-center p-8 bg-app-text/30 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            onClick={e => e.stopPropagation()}
            role="dialog" aria-modal="true" aria-label="This one is pinned"
            className="bg-white border border-app-border rounded-3xl px-7 py-7 max-w-xs w-full shadow-2xl text-center"
          >
            <div className="w-12 h-12 rounded-full bg-gold/10 text-gold flex items-center justify-center mx-auto mb-4">
              <Lock className="w-5 h-5" />
            </div>
            <p className="font-serif text-xl font-bold text-app-text mb-2">This one is pinned</p>
            <p className="ds-body-sm text-app-sub/70 leading-relaxed">
              {pinnedNotice.journey
                ? 'It\u2019s a booked journey, so the rest of the day moves around it. Change the booking in Itinerary to move it.'
                : 'It\u2019s pinned to its time, so the rest of the day moves around it. Tap the time to unpin it.'}
            </p>
            <button
              onClick={() => setPinnedNotice(null)}
              className="w-full mt-6 py-3.5 rounded-xl bg-gold text-white text-[11px] font-black uppercase tracking-widest shadow-lg shadow-gold/20 active:scale-[0.98] transition"
            >
              Got it
            </button>
          </motion.div>
        </motion.div>,
        document.getElementById('import-modal-root') ?? document.body
      )}
      {/* ── Auto-fill banner — shown whenever THIS day is empty (not just
           when the whole trip is blank), replacing the day-title block
           below for that day. Sits directly under DestinationStrip with
           zero gap: square top corners (matches the strip), rounded
           bottom. Solid white, not blobs — this is the "nothing here yet"
           state, so it reads as a plain CTA rather than the "day has a
           plan" treatment the title block below uses. */}
      {/* A plain div, not a button: the card carries its own explicit CTA,
          and a whole-card tap target gave no clue what tapping would do. */}
      {dayEmpty && (
          <div className="relative w-full overflow-hidden bg-white shadow-sm rounded-b-2xl">
            <div className="relative z-10 p-5 space-y-4">
              <div className="flex items-start gap-3.5">
                <div className="w-12 h-12 rounded-2xl bg-gold/10 flex items-center justify-center shrink-0"><Sparkles className="w-6 h-6 text-gold" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-black text-app-text">Auto-fill your schedule</div>
                  <div className="text-xs font-medium text-app-sub/60 mt-1 leading-relaxed">
                    Turns your shortlist into a day-by-day plan for {destName}, tailored to your travel style and interests.
                  </div>
                </div>
              </div>
              <button onClick={onAutoFillTrip}
                className="w-full py-2.5 rounded-xl border border-app-border bg-white/70 text-app-sub text-[10px] font-black uppercase tracking-wider hover:bg-white hover:text-gold transition flex items-center justify-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 shrink-0" /> Get started
              </button>
            </div>
          </div>
      )}

      {[activeDay].map(() => (
        <div key={d.date} className="space-y-3">
          {(dayBadge || d.title) && !dayEmpty && (
            /* Joined to DestinationStrip above (zero gap, square corners
               here vs. its rounded top), floating the SAME colour(s) as the
               strip (headerTints, from Itinerary.tsx) so it reads as that
               colour continuing underneath, not an unrelated set of hues
               fighting the strip above it.
               Rounded bottom corners (top stays square to fuse with the
               strip) — only shown once the day actually has content; an
               empty day shows the auto-fill banner above instead. */
            /* A flat wash of the destination strip's own colour, so this
               reads as that band continuing underneath. It used to carry
               drifting blobs of the same hues; against a page that now
               drifts on its own that was two motions fighting, and the card
               is short enough that a blob crossing it is mostly edge. */
            <div className="relative overflow-hidden bg-white shadow-sm rounded-b-2xl" style={{ contain: 'paint' }}>
              <HeaderTint tints={headerTints} />
              <div className="relative z-10 p-4 space-y-3">
                <div>
                  {dayBadge && (
                    <div className="text-[10px] font-black uppercase tracking-widest text-app-sub/50 mb-1">{dayBadge}</div>
                  )}
                  {d.title && (
                    <div className="font-serif text-2xl font-bold text-app-text leading-tight text-balance">{d.title}</div>
                  )}
                </div>

                {/* Plan-level actions. Both regenerate the whole
                    destination's plan, so they only appear once one exists
                    — on a blank plan the auto-fill banner above is the
                    single call to action and it opens the wizard itself. */}
                {!allEmpty && (
                  <div className="flex items-stretch gap-2">
                    {onOpenPreferences && (
                      <button onClick={onOpenPreferences}
                        className="flex-1 py-2.5 rounded-xl border border-app-border bg-white/70 text-app-sub text-[10px] font-black uppercase tracking-wider hover:bg-white hover:text-gold transition flex items-center justify-center gap-1.5">
                        <Settings className="w-3.5 h-3.5 shrink-0" /> Preferences
                      </button>
                    )}
                    <button
                      onClick={() => setConfirmRefresh({ title: 'Refresh plan?', message: 'This replaces your whole plan with a fresh one, built around your shortlist and preferences.' })}
                      className="flex-1 py-2.5 rounded-xl border border-app-border bg-white/70 text-app-sub text-[10px] font-black uppercase tracking-wider hover:bg-white hover:text-gold transition flex items-center justify-center gap-1.5">
                      <RefreshCw className="w-3.5 h-3.5 shrink-0" /> Refresh Plan
                    </button>
                  </div>
                )}

              </div>
            </div>
          )}
          {/* One notice at a time: before generation, show what's missing;
              afterwards, explain the assumptions the saved plan used. */}
          {!hasGenerated && gapHeadline && (() => {
            const body = <>
              <div className="ds-body">{gapHeadline}</div>
              <div className="text-xs font-medium text-app-sub/70 leading-relaxed mt-1">
                Finish adding this to your itinerary before creating your schedule, for best results.
              </div>
            </>;
            const box = 'w-full text-left rounded-2xl border border-app-border/40 bg-app-faint/70 px-4 py-3.5';
            // Only transport/accommodation gaps have an Itinerary Gaps modal.
            return itineraryGapsToOpen ? (
              <button className={`${box} flex items-center gap-3 hover:bg-app-faint transition-colors`} onClick={() => {
                window.dispatchEvent(new CustomEvent('nav-tab', { detail: { tab: 'logistics' } }));
                openDeepLink({ tab: 'logistics', section: 'gaps' });
              }}>
                <div className="flex-1 min-w-0">{body}</div>
                <ArrowRight className="w-4 h-4 text-app-sub/50 shrink-0" />
              </button>
            ) : <div className={box}>{body}</div>;
          })()}
          {hasGenerated && (assumedSlots.length > 0 || missingAccommodation) && (
              <button className="w-full text-left rounded-2xl border border-app-border/40 bg-app-faint/70 px-4 py-3.5 flex items-center gap-3 hover:bg-app-faint transition-colors"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('nav-tab', { detail: { tab: 'logistics' } }));
                  openDeepLink({ tab: 'logistics', section: 'gaps' });
                }}>
                <div className="flex-1 min-w-0">
                  <div className="ds-body">Planned around some assumptions</div>
                  <div className="mt-1 text-xs font-medium text-app-sub/70 leading-relaxed">
                    {assumedSlots.length > 0 && 'Journey cards below show what’s known and mark missing details as provisional.'}
                    {missingAccommodation && ' Add your accommodation address for accurate transfers.'}
                    {' '}Update missing details in Itinerary, then refresh your plan.
                  </div>
                  <div className="mt-2 text-sm font-semibold text-app-text">Update Itinerary</div>
                </div>
                <ArrowRight className="w-4 h-4 text-app-sub/50 shrink-0" />
              </button>
          )}
          {hasGenerated && unplaced.length > 0 && (
            <button onClick={() => setShowPending(true)}
              className="w-full text-left rounded-2xl border border-app-border/40 bg-app-faint/70 px-4 py-3.5 flex items-center gap-3 hover:bg-app-faint transition-colors">
              <div className="flex-1 min-w-0">
                <div className="ds-body">{unplaced.length} committed Shortlist {unplaced.length === 1 ? 'item' : 'items'} not yet in your schedule</div>
                <div className="text-xs font-medium text-app-sub/70 leading-relaxed mt-1">Review them before refreshing your plan.</div>
              </div>
              <ArrowRight className="w-4 h-4 text-app-sub/50 shrink-0" />
            </button>
          )}
          {orderedAll.map((s: any, si: number) => {
              const isTravel = s.type === 'travel';
              // Non-annotation travel slots are pruned at load; belt-and-braces skip.
              if (isTravel && !s.journey && !isTravelAnnotation(s.title)) return null;
              const Icon = isTravel
                ? (/^check[\s-]?in|^check[\s-]?out/i.test(s.title || '') ? BedDouble
                  : /^arriv/i.test(s.title || '') ? MapPin
                  : TRAVEL_KIND_ICON[s.legKind] || L1_BY_KEY.transport.icon)
                : (SLOT_ICON[normalizeSlotType(s.type)] || L1_BY_KEY.other.icon);
              const isEditing = editing === s.id;
              const chosen = typeof s.chosen === 'number' ? s.chosen : -1;
              const legKey = `${d.date}-${si}`;
              const overlaps = overlapIds.has(s.id);
              // Linked slots show the itinerary item's CURRENT name (read-time sync).
              const linkedAct = s.activityId ? availableActs.find((a: any) => a.id === s.activityId) : null;
              const showMainSourceBadge = linkedAct?.type === 'transport' || !(
                Array.isArray(s.options) && s.options.some((o: any) => o.shortlistId === s.activityId)
              );
              const displayTitle = s.title || linkedAct?.name;
              // A booking for only some travellers is an interruption for them, not everyone's plan.
              const onlyFor = linkedAct?.participantIds && <div className="text-xs font-medium text-app-sub mt-0.5">
                {linkedAct.participantIds.map((id: string) => travellers.find(t => t.id === id)?.name.split(' ')[0]).filter(Boolean).join(', ') || 'Some travellers'} only</div>;
              const isSlotExpanded = expandedSlot === s.id;
              const clockChange = clockChangeLabel(orderedAll[si - 1], s);
              return (
                // layout: the rows are keyed by slot id, so when moveSlot
                // reorders the array motion measures each row's old and new
                // box and tweens between them — the two cards visibly trade
                // places instead of blinking into their new order. Without
                // it the only feedback that anything happened was the times
                // changing, which is easy to miss on a long day.
                <motion.div
                  key={s.id ?? si}
                  layout="position"
                  transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                >
                  {/* ── Time divider (tap to edit) ── */}
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2">
                    <div className="flex items-center gap-2">
                    <button onClick={() => isEditing ? closeSlotEdit(di, s.id) : openSlotEdit(di, s.id)}
                      className={`text-sm font-bold tabular-nums px-1.5 py-0.5 rounded-md transition-colors whitespace-nowrap ${isEditing ? 'bg-gold text-white' : overlaps ? 'text-red-600 hover:bg-app-faint' : 'text-app-sub/70 hover:bg-app-faint'}`}>
                      {s.estimatedTravel ? `Est. ${formatClock(s.start)}` : formatClock(s.start)}
                    </button>
                    {clockChange && <span className="text-sm font-normal text-app-sub/70 whitespace-nowrap">{clockChange}</span>}
                    {s.locked && !isEditing && <Lock className="w-3.5 h-3.5 text-app-sub/70 shrink-0 -ml-1" strokeWidth={2.5} />}
                    </div>
                    {overlaps && (
                      <span title="Overlaps the next spot" className="flex items-center gap-1 text-[9px] font-black uppercase tracking-wider text-red-700 bg-red-100 border border-red-500 px-1.5 py-0.5 rounded-full">
                        <AlertTriangle className="w-2.5 h-2.5" /> Overlap
                      </span>
                    )}
                    <div className="flex-1 h-px bg-app-border/30" />
                    <div className="flex items-center gap-1">
                      {/* Reorder: trades places with the nearest MOVABLE
                          neighbour, skipping right over a locked slot
                          rather than swapping with it. Only shown at all when
                          there's a neighbour to cross. At an edge, an activity
                          can sit just before/after a fixed booking.

                          On a locked slot the buttons still show,
                          dimmed, and say why instead of doing nothing. They
                          used to be hidden entirely, which left the one slot
                          a traveller can't reorder as the one slot with no
                          explanation of why. */}
                      {(['up', 'down'] as const).map(dir => {
                        const pinned = !!s.locked;
                        if (!pinned && !canMoveSlot(di, s.id, dir)) return null;
                        const Chevron = dir === 'up' ? ChevronUp : ChevronDown;
                        return (
                          <button
                            key={dir}
                            onClick={() => pinned ? setPinnedNotice({ journey: !!s.journey }) : moveSlot(di, s.id, dir)}
                            title={pinned ? 'Why this can’t move' : dir === 'up' ? 'Move earlier' : 'Move later'}
                            aria-label={pinned ? 'Why this can’t move' : dir === 'up' ? 'Move earlier' : 'Move later'}
                            className={`w-7 h-7 flex items-center justify-center rounded-lg active:scale-95 transition-all ${
                              pinned ? 'text-app-sub/20 hover:text-app-sub/40' : 'text-app-sub/40 hover:text-gold hover:bg-app-faint'
                            }`}
                          >
                            <Chevron className="w-4 h-4" />
                          </button>
                        );
                      })}
                      <button aria-label="Add a spot here" onClick={() => openAdd(s.start)} title="Add a spot here"
                        className="w-9 h-9 flex items-center justify-center rounded-xl text-app-sub/50 hover:text-gold hover:bg-app-faint active:scale-95 transition">
                        <Plus className="w-[18px] h-[18px]" />
                      </button>
                      <button aria-label="Edit this spot" onClick={() => isEditing ? closeSlotEdit(di, s.id) : openSlotEdit(di, s.id)} title="Edit this spot"
                        className="w-9 h-9 flex items-center justify-center rounded-xl text-app-sub/50 hover:text-gold hover:bg-app-faint active:scale-95 transition">
                        <Pencil className="w-[17px] h-[17px]" />
                      </button>
                    </div>
                  </div>

                  {isEditing && (() => {
                    const linkedAct = s.activityId ? availableActs.find((a: any) => a.id === s.activityId) : null;
                    return (
                    <div className="mb-3 p-4 bg-app-faint rounded-2xl border border-app-border/40">
                      {s.estimatedTravel ? (
                        <div className="space-y-3">
                          <label className="ds-label block" htmlFor={`estimate-start-${s.id}`}>Provisional start time</label>
                          <input id={`estimate-start-${s.id}`} type="time" value={s.start} step={300}
                            onChange={e => updateSlot(di, s.id, { start: e.target.value })}
                            className={`${FIELD} border-app-border/60`} />
                          <p className="text-sm text-app-sub">Route and duration come from Itinerary.</p>
                          <button onClick={() => closeSlotEdit(di, s.id)} className="min-h-11 text-sm font-semibold text-app-text underline">Done</button>
                        </div>
                      ) : <SlotForm
                        mode="edit"
                        initial={s}
                        destName={destName}
                        linkedName={linkedAct?.name}
                        linkedLocation={linkedAct?.address}
                        onViewItinerary={onExitToItinerary ? (id) => onExitToItinerary(id) : undefined}
                        onChange={patch => updateSlot(di, s.id, patch)}
                        onDelete={() => deleteSlot(di, s.id)}
                        onClose={() => closeSlotEdit(di, s.id)}
                      />}
                    </div>
                    );
                  })()}

                  {/* ── Arrival / departure / journey annotation — expand/collapse like
                       activity cards; details live in the expanded view. ── */}
                  {isTravel ? (() => {
                    // Description shows on the card (like activity cards); only the
                    // location lives behind the expander.
                    const hasMore = !!(s.location && s.location.trim());
                    return (
                    // A placeholder (assumed arrival/departure, or a check-in
                    // with no address yet) is drawn dashed and flat, so it reads
                    // as a slot waiting on the itinerary rather than a fact.
                    <div className={s.placeholder
                      ? 'bg-app-faint/40 rounded-2xl border border-dashed border-app-border'
                      : 'surface-solid bg-white rounded-2xl border border-app-border/40 shadow-sm'}>
                      <div
                        className={`p-4 transition-colors ${hasMore ? 'cursor-pointer active:bg-app-faint' : ''}`}
                        onClick={hasMore ? () => setExpandedSlot(expandedSlot === s.id ? null : s.id) : undefined}
                      >
                        <div className="flex items-start gap-2.5">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${s.placeholder ? 'bg-app-border/30' : 'bg-gold/10'}`}><Icon className={`w-4 h-4 ${s.placeholder ? 'text-app-sub/60' : 'text-gold'}`} /></div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-black text-app-text leading-tight">{s.title}</div>
                            {s.durationMin > 0 && !s.estimatedTravel && <div className="text-sm font-normal text-app-sub/70 tabular-nums mt-0.5">{fmtDur(s.durationMin)}</div>}
                            {s.subtitle && <p className="text-xs font-normal text-app-sub/70 leading-relaxed mt-1.5">{s.subtitle}</p>}
                          </div>
                          {hasMore && <ChevronDown className={`w-4 h-4 text-app-sub/30 shrink-0 mt-0.5 transition-transform ${isSlotExpanded ? 'rotate-180' : ''}`} />}
                        </div>
                        {hasMore && isSlotExpanded && (
                          <div className="mt-3 pt-3 border-t border-app-border/20 space-y-2" onClick={e => e.stopPropagation()}>
                            <div className="flex items-start gap-1.5 text-sm text-app-sub">
                              <MapPin className="w-3 h-3 shrink-0 mt-0.5 text-gold/60" />
                              <span>{s.location}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })() : (
                  /* ── Full-width activity card ── */
                  <div className="surface-solid bg-white rounded-2xl border border-app-border/40 shadow-sm">
                    {linkedAct && onExitToItinerary ? (
                      /* Linked to a real Shortlist / Logistics item — the
                         card opens that item's own portal view directly
                         rather than expanding in place (a separate "View
                         saved item" link was one tap too many for something
                         already one tap away). Editing the slot's own time/
                         duration/note is still the pencil button above. */
                      <button
                        onClick={() => onExitToItinerary(s.activityId)}
                        className="w-full text-left p-4 hover:bg-app-faint active:bg-app-faint transition-colors flex items-start gap-2.5"
                      >
                        <div className="w-7 h-7 rounded-full bg-gold/10 flex items-center justify-center shrink-0 mt-0.5"><Icon className="w-4 h-4 text-gold" /></div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-black text-app-text leading-tight">{displayTitle}</div>
                          {onlyFor}
                          {(s.rating > 0 || showMainSourceBadge) && <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {s.rating > 0 && <StarRating rating={s.rating} />}
                            {showMainSourceBadge && <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-gold bg-gold/10 border border-gold/30 px-2 py-0.5 rounded-full">
                              {linkedAct.type === 'transport' ? 'From Logistics' : <><Star className="w-2.5 h-2.5" /> From Shortlist</>}
                            </span>}
                          </div>}
                          {s.durationMin > 0 && <div className="text-sm font-normal text-app-sub/70 tabular-nums mt-0.5">{fmtDur(s.durationMin)}</div>}
                          {s.desc && <p className="text-xs font-normal text-app-sub/70 leading-relaxed mt-1.5">{s.desc}</p>}
                        </div>
                        <ChevronRight className="w-4 h-4 text-app-sub/30 shrink-0 mt-0.5" />
                      </button>
                    ) : (
                    /* Tappable header — collapses / expands detail */
                    <div
                      className="p-4 cursor-pointer active:bg-app-faint transition-colors"
                      onClick={() => setExpandedSlot(expandedSlot === s.id ? null : s.id)}
                    >
                      <div className="flex items-start gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-gold/10 flex items-center justify-center shrink-0 mt-0.5"><Icon className="w-4 h-4 text-gold" /></div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-black text-app-text leading-tight">{displayTitle}</div>
                          {onlyFor}
                          {s.rating > 0 && (
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <StarRating rating={s.rating} />
                            </div>
                          )}
                          {s.durationMin > 0 && <div className="text-sm font-normal text-app-sub/70 tabular-nums mt-0.5">{fmtDur(s.durationMin)}</div>}
                          {s.desc && <p className="text-xs font-normal text-app-sub/70 leading-relaxed mt-1.5">{s.desc}</p>}
                        </div>
                        <ChevronDown className={`w-4 h-4 text-app-sub/30 shrink-0 mt-0.5 transition-transform ${isSlotExpanded ? 'rotate-180' : ''}`} />
                      </div>
                      {/* Expanded detail panel */}
                      {isSlotExpanded && (
                        <div className="mt-3 pt-3 border-t border-app-border/20 space-y-2" onClick={e => e.stopPropagation()}>
                          {s.note && <p className="text-xs font-normal text-app-sub/70 leading-relaxed">{s.note}</p>}
                          {s.location && !s.options?.length && (
                            <div className="flex items-start gap-1.5 text-sm text-app-sub">
                              <MapPin className="w-3 h-3 shrink-0 mt-0.5 text-gold/60" />
                              <span>{s.location}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    )}
                    {/* ── Pick a spot carousel ──────────────────────────────
                         Shows for any slot with options or a pending fetch.
                         Options tagged with shortlistId are treated as linked
                         Shortlist items and rendered with the "From Shortlist"
                         pill to the right of the star rating. */}
                    {(() => {
                      const visibleOptions = (Array.isArray(s.options) ? s.options : [])
                        .map((o: any, oi: number) => ({ o, oi }))
                        .filter(({ o, oi }: any) => !isUnsuitableBreakfastOption(s.title || '', o)
                          || (o.shortlistId && s.activityId === o.shortlistId) || chosen === oi);
                      const hasOptions = visibleOptions.length > 0;
                      const isLoading = suggestLoading.has(s.id) && !hasOptions;
                      if (!hasOptions && !isLoading) return null;
                      return (
                        <div className="px-4 pb-4 -mt-1">
                          <div className="text-[9px] font-black uppercase tracking-widest text-app-sub/40 mb-1.5">Pick a spot</div>
                          <div className="flex items-stretch gap-3 -mx-4 px-4 overflow-x-auto no-scrollbar pb-2 snap-x snap-mandatory">
                            {hasOptions && visibleOptions
                              // Selected option first — no gold ring anymore (it was
                              // getting clipped at the top by this scroll container),
                              // so leading position is now the only "you picked this"
                              // signal besides the green tick.
                              .sort((a, b) => {
                                const sel = (x: { o: any; oi: number }) => {
                                  const slAct = x.o.shortlistId ? availableActs.find((act: any) => act.id === x.o.shortlistId) : null;
                                  return slAct ? s.activityId === x.o.shortlistId : (chosen === x.oi && !s.activityId);
                                };
                                return (sel(b) ? 1 : 0) - (sel(a) ? 1 : 0);
                              })
                              .map(({ o, oi }) => {
                              const slAct = o.shortlistId ? availableActs.find((a: any) => a.id === o.shortlistId) : null;
                              const isShortlist = !!slAct;
                              const slChosen = isShortlist && s.activityId === o.shortlistId;
                              const optChosen = !isShortlist && chosen === oi && !s.activityId;
                              const isSelected = slChosen || optChosen;
                              return (
                                <div key={oi} className="shrink-0 w-[88%] snap-center rounded-xl overflow-hidden">
                                  <button
                                    onClick={e => {
                                      e.stopPropagation();
                                      if (isShortlist) {
                                        if (slChosen) {
                                          updateSlot(di, s.id, { activityId: null, chosen: -1 });
                                        } else {
                                          updateSlot(di, s.id, { activityId: o.shortlistId, chosen: -1 });
                                        }
                                        return;
                                      }
                                      if (optChosen) { updateSlot(di, s.id, { chosen: -1 }); return; }
                                      // Pick a spot selects a route anchor in this schedule
                                      // only. Saving to Shortlist is a separate, explicit action.
                                      updateSlot(di, s.id, { chosen: oi, activityId: null });
                                    }}
                                    className="w-full h-full text-left p-4 bg-gold/[0.07]"
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <h4 className="text-base font-black text-app-text font-serif italic leading-tight text-balance flex-1">{o.title}</h4>
                                      <div className={`relative w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all ${isSelected ? 'bg-emerald-500 text-white' : 'bg-gold/80 text-white'}`}>
                                        {isSelected ? <Check className="w-3.5 h-3.5 stroke-[3] shrink-0" /> : <Plus className="w-3.5 h-3.5 shrink-0" />}
                                      </div>
                                    </div>
                                    {/* Stars + 'From Shortlist' pill inline */}
                                    {(o.rating > 0 || isShortlist) && (
                                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                        {o.rating > 0 && <StarRating rating={o.rating} />}
                                        {isShortlist && (
                                          <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-gold bg-gold/10 border border-gold/30 px-2 py-0.5 rounded-full">
                                            <Star className="w-2.5 h-2.5" /> From Shortlist
                                          </span>
                                        )}
                                      </div>
                                    )}
                                    {o.address && (
                                      <div className="flex items-start gap-1.5 text-app-sub/60 mt-2">
                                        <MapPin className="w-3 h-3 shrink-0 mt-0.5" />
                                        <span className="text-[13px] font-normal leading-snug">{o.address}</span>
                                      </div>
                                    )}
                                    {o.desc && <p className="text-xs font-normal text-app-sub/70 leading-relaxed mt-2.5 line-clamp-3">{o.desc}</p>}
                                  </button>
                                </div>
                              );
                            })}
                            {isLoading && (
                              <div className="shrink-0 w-[88%] snap-center rounded-xl overflow-hidden">
                                <div className="p-4 bg-app-faint flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gold/60">
                                  <span className="w-3.5 h-3.5 border-2 border-gold/40 border-t-transparent rounded-full animate-spin" /> Finding spots…
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  )}

                  {/* ── Gap prompt: a long free stretch before the next slot ── */}
                  {(() => {
                    // Free blocks already show their own "Add plans" CTA above.
                    if (isTravel || s.type === 'free') return null;
                    // Measure against the NEXT slot in the full sequence (incl. travel
                    // journey items) — otherwise an arrival-day morning activity
                    // measures all the way to the first destination activity and
                    // reports the whole journey as a bogus multi-hour "free" gap.
                    const ordered = reSort(d.slots || []);
                    const pos = ordered.findIndex((x: any) => x.id === s.id);
                    const nxt = ordered[pos + 1];
                    if (!nxt) return null;
                    // Don't offer destination "ideas" into a journey (the free time is
                    // at the origin, and suggestions would be for the wrong city).
                    if (nxt.type === 'travel') return null;
                    const gapStart = toMin(s.start) + (s.durationMin || 0);
                    const gap = scheduleMinute(nxt) - scheduleMinute(s) - (s.durationMin || 0) - legMin(s, nxt);
                    if (gap < GAP_MIN) return null;
                    return (
                      <>
                        {/* Timestamp divider — same treatment as the per-slot
                            dividers, so this free block still reads as its
                            own separate slot in the timeline rather than
                            trailing off the activity above it. */}
                        <div className="flex items-center gap-2 py-2">
                          <span className="text-sm font-bold tabular-nums px-1.5 py-0.5 text-app-sub/70 whitespace-nowrap">{stampAt(gapStart)}</span>
                        </div>
                        <button onClick={() => openAdd(snap5(toHHMM(gapStart)), 'ideas')}
                          className="w-full my-1 px-2 py-3 rounded-2xl border-2 border-dashed border-gold/30 bg-gold/[0.04] text-gold hover:bg-gold/10 hover:border-gold/50 active:scale-[0.99] transition flex items-center justify-center gap-1.5">
                          <Sparkles className="w-4 h-4 shrink-0" />
                          <span className="text-sm font-normal whitespace-nowrap">{fmtDur(gap)} free · <span className="font-bold">Talisman Explorer</span></span>
                        </button>
                      </>
                    );
                  })()}

                  {/* ── Transport connector to the next place ──
                       Booked leg (flight/train/ferry on a journey item) → a fixed
                       gold leg with carrier + duration, no map. Known endpoints
                       (geocodable) → auto transit estimate + collapsible route map.
                       Unknown location → add transport mode + duration by hand.
                       Journey items (Depart → Arrive → Check-in) each get the
                       connector to the next step in the sequence. */}
                  {(() => {
                    // Explicit booked transport (the actual flight/train/ferry) rides
                    // on the Depart item — render it as a static gold interceding leg
                    // (no driving map, which would be wrong for rail/air).
                    if (s.legAfter) {
                      const L = s.legAfter;
                      const LegIcon = TRAVEL_KIND_ICON[L.kind] || Plane;
                      const boardMin = toMin(s.start) + (s.durationMin || 0);
                      const canDeepLink = !!(L.linkedId && onExitToItinerary);
                      const legExpanded = expandedLeg === legKey;
                      return (
                        <div className="flex flex-col items-center gap-0.5">
                          <div className="w-full flex items-center gap-2.5 pt-2 pb-0.5">
                            <span className="text-sm font-bold tabular-nums px-1.5 py-0.5 rounded-md text-app-sub/70 whitespace-nowrap">{stampAt(boardMin)}</span>
                            <div className="flex-1 h-px bg-app-border/20" />
                          </div>
                          <button
                            onClick={canDeepLink ? () => setExpandedLeg(legExpanded ? null : legKey) : undefined}
                            className={`py-1 flex flex-wrap justify-center items-center gap-1.5 text-gold text-[13px] font-bold ${canDeepLink ? 'hover:opacity-80 transition-opacity' : 'cursor-default'}`}>
                            <LegIcon className="w-3.5 h-3.5" />
                            <span>{L.label}</span>
                            {L.durationMin > 0 && <span className="text-gold/60 font-normal">· {fmtDur(L.durationMin)}</span>}
                            {canDeepLink && <ChevronDown className={`w-3 h-3 transition-transform ${legExpanded ? 'rotate-180' : ''}`} />}
                          </button>
                          {L.route && <div className="text-sm font-medium text-app-sub pb-1">{L.route}</div>}
                          {canDeepLink && legExpanded && (
                            <button onClick={() => onExitToItinerary!(L.linkedId)}
                              className="mb-1 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-gold hover:opacity-80 transition-opacity">
                              <LinkIcon className="w-3 h-3" /> View saved item
                            </button>
                          )}
                        </div>
                      );
                    }
                    const nextAct = nextSlotOf[s.id];
                    // Only the genuine end of the day (no following slot) has no
                    // connector. EVERY other consecutive pair gets one: a geocodable
                    // pair auto-estimates a leg; anything else (a free block, the
                    // departure marker, or a slot missing a location) falls through to
                    // a manual "Add transport" line — so there is ALWAYS something
                    // between two cards.
                    if (!nextAct) return null;
                    const qA = slotQuery(s), qB = slotQuery(nextAct);
                    const cA = slotCoords(s), cB = slotCoords(nextAct);
                    // An auto-estimate only counts as "known" if it's plausible for a
                    // same-day hop. A multi-hour result almost always means a bad
                    // geocode (e.g. a generic "Local Cafe" resolving to the wrong
                    // city) — in that case we fall through to a manual "Add transport"
                    // line rather than show an absurd "13h walk".
                    const estByCoords = (cA && cB) ? legEstimate(cA, cB, GEMINI_MODE[s.travelMode]) : null;
                    const known = !!(qA && qB && cA && cB) && !!estByCoords && estByCoords.durationMin <= MAX_AUTO_LEG_MIN;
                    const editingT = editingTransit === s.id;
                    const nextStartMin = scheduleMinute(nextAct);
                    // "Leave by" time shown above the leg. Rounding the
                    // SUBTRACTED RESULT up (stampAt(nextStartMin - legMin))
                    // was the bug: a 13-min drive rounded the departure time
                    // itself up toward the arrival, shrinking the displayed
                    // gap to 10 min — the opposite of "always round up".
                    // Round the duration up to a 5-min figure FIRST, then
                    // subtract that from the (already grid-aligned) next
                    // start — a 13-min drive becomes a clean 15-min gap. The
                    // leg chip still shows the real, unrounded duration.
                    const leaveBy = (legMin: number) => formatClock(shiftScheduleTime(s, nextStartMin - round5(legMin)).start!);

                    if (known) {
                      // legRoutes[route identity] is Google's real routed duration + mode
                      // (fetched by the effect above). Until it lands, fall back
                      // to legEstimate's straight-line guess — shown with its
                      // "Est." prefix so it reads as provisional.
                      const est = legEstimate(cA, cB, GEMINI_MODE[s.travelMode]);
                      const route = legRoutes[`${legKey}:${legRouteKey(cA, cB, GEMINI_MODE[s.travelMode], transportPref)}`];
                      const mode = route?.mode ?? est.mode;
                      const durationMin = route?.durationMin ?? est.durationMin;
                      if (durationMin <= 0) return null;
                      const info = MODE_INFO[mode];
                      const ModeIcon = info.Icon;
                      const label = route ? `${fmtDur(durationMin)} ${info.word}` : est.label;
                      return (
                        <div className="flex flex-col items-center gap-1">
                          {durationMin > 0 && (
                            <div className="w-full flex items-center gap-2.5 pt-2 pb-0.5">
                              <span className="text-sm font-bold tabular-nums px-1.5 py-0.5 rounded-md text-app-sub/70 whitespace-nowrap">
                                {leaveBy(durationMin)}
                              </span>
                              <div className="flex-1 h-px bg-app-border/20" />
                            </div>
                          )}
                          <button onClick={() => setExpandedLeg(expandedLeg === legKey ? null : legKey)}
                            className="py-1.5 flex flex-wrap justify-center items-center gap-1.5 text-gold text-[13px] font-bold hover:opacity-80 transition-opacity">
                            <ModeIcon className="w-3.5 h-3.5" />
                            {label}
                            <ChevronDown className={`w-3 h-3 transition-transform ${expandedLeg === legKey ? 'rotate-180' : ''}`} />
                          </button>
                          {expandedLeg === legKey && (
                            <div className="w-full relative rounded-2xl overflow-hidden border border-app-border/30 aspect-[16/8.5] bg-app-faint">
                              {route?.result
                                ? <LegRouteMap result={route.result} />
                                : <iframe width="100%" height="100%" loading="lazy" allowFullScreen
                                    src={embedDirectionsUrl({ origin: qA!, destination: qB!, mode })}
                                    className="border-0" />}
                              {/* The map is a preview, not a thing to pan — tapping
                                  anywhere on it offers to hand the route off to a
                                  real maps app. */}
                              {cA && cB && (
                                <button
                                  onClick={() => setDirectionsFor({ a: cA, b: cB, mode })}
                                  className="absolute inset-0 z-10 flex items-end justify-end p-2 group"
                                >
                                  <span className="inline-flex items-center gap-1 bg-white/95 backdrop-blur-sm rounded-full px-2.5 py-1 shadow-md text-[9px] font-black uppercase tracking-widest text-app-text group-hover:text-gold transition-colors">
                                    <ExternalLink className="w-3 h-3" /> Open
                                  </span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }

                    // Unknown location (incl. arrival annotations) → manual or itinerary-linked transport
                    const t = s.transit;
                    const tMode = TRANSIT_MODES.find(m => m.v === t?.mode) ?? TRANSIT_MODES[0];
                    const addingT = addingTransit === s.id;
                    const itinTransports = availableActs.filter((a: any) => a.type === 'transport');
                    const saveTransit = (val: any) => {
                      const arrivalMin = Math.ceil((scheduleMinute(s) + (s.durationMin || 0) + val.durationMin) / 5) * 5;
                      const updatedSlots = reSort(d.slots.map((x: any) => {
                        if (x.id === s.id) return { ...x, transit: val };
                        // Push the next slot out to fit the leg — but never move a
                        // locked slot (e.g. the fixed departure time / journey items).
                        if (x.id === nextAct.id && s.durationMin > 0 && !nextAct.locked) return shiftScheduleTime(x, arrivalMin);
                        return x;
                      }));
                      onChange({ ...schedule, days: days.map((dd: any, i: number) => i !== di ? dd : { ...dd, slots: updatedSlots }) });
                    };
                    // Map itinerary transport category → TRANSIT_MODES key
                    const modeFromCat = (cat?: string): string => {
                      if (!cat) return 'transit';
                      const c = cat.toLowerCase();
                      if (/walk|foot/.test(c)) return 'walking';
                      if (/car|taxi|driv|rideshare|uber|lyft/.test(c)) return 'driving';
                      return 'transit';
                    };
                    return (
                      <div className="flex flex-col items-center gap-1">
                        {t?.durationMin > 0 && !editingT && !addingT && (
                          <div className="w-full flex items-center gap-2.5 pt-2 pb-0.5">
                            <span className="text-sm font-bold tabular-nums px-1.5 py-0.5 rounded-md text-app-sub/70 whitespace-nowrap">
                              {leaveBy(t.durationMin)}
                            </span>
                            <div className="flex-1 h-px bg-app-border/20" />
                          </div>
                        )}
                        {editingT ? (
                          <TransitEditor
                            initial={t}
                            onSave={val => {
                              // Preserve the itinerary link (name + id) when editing duration.
                              saveTransit({ ...val, ...(t?.linkedName ? { linkedName: t.linkedName } : {}), ...(t?.linkedId ? { linkedId: t.linkedId } : {}) });
                              setEditingTransit(null);
                            }}
                            onClear={() => { updateSlot(di, s.id, { transit: null }); setEditingTransit(null); }}
                            onCancel={() => setEditingTransit(null)}
                          />
                        ) : addingT ? (
                          // Add transport panel: itinerary items + manual fallback
                          <div className="w-full pt-1 pb-0.5 space-y-1.5">
                            {itinTransports.length > 0 && (
                              <>
                                <div className="text-[9px] font-black uppercase tracking-widest text-app-sub/40 mb-0.5">Link from itinerary</div>
                                {itinTransports.map((tr: any) => (
                                  <button key={tr.id}
                                    onClick={() => {
                                      // calculatedDurationMinutes resolves each end's actual
                                      // timezone (airport/station code or city) rather than
                                      // assuming both share one — the same logic Itinerary.tsx's
                                      // transport detail view uses, so a linked leg's duration
                                      // here always matches what's shown there.
                                      const durationMin = calculatedDurationMinutes(tr, tr.dep, tr.arr, tr.depTz, tr.arrTz) ?? 0;
                                      saveTransit({ mode: modeFromCat(tr.category), durationMin, linkedName: tr.name, linkedId: tr.id });
                                      setAddingTransit(null);
                                    }}
                                    className="w-full text-left px-3 py-2.5 rounded-xl bg-white border border-app-border/40 hover:border-gold/50 active:scale-[0.99] transition flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                      <div className="text-[11px] font-black text-app-text leading-tight">{tr.name}</div>
                                      {tr.category && <div className="text-[9px] font-bold text-app-sub/50 uppercase tracking-wide mt-0.5">{tr.category}</div>}
                                    </div>
                                    <Plus className="w-3.5 h-3.5 text-gold shrink-0" />
                                  </button>
                                ))}
                                <div className="text-[9px] font-black uppercase tracking-widest text-app-sub/25 py-0.5 text-center">— or —</div>
                              </>
                            )}
                            <button
                              onClick={() => { setAddingTransit(null); setEditingTransit(s.id); }}
                              className="w-full text-center px-3 py-2 rounded-xl border border-app-border/40 text-[10px] font-black uppercase tracking-widest text-app-sub/60 hover:border-gold/40 hover:text-gold transition">
                              Enter manually
                            </button>
                            <button onClick={() => setAddingTransit(null)}
                              className="w-full text-center py-1 text-[9px] font-black uppercase tracking-widest text-app-sub/30 hover:text-app-sub/60 transition-colors">
                              Cancel
                            </button>
                          </div>
                        ) : t ? (
                          <>
                            {t.linkedName ? (
                              // Itinerary-linked leg: tap to expand (like the auto legs),
                              // revealing the deep link + duration edit.
                              <button onClick={() => setExpandedLeg(expandedLeg === legKey ? null : legKey)}
                                className="py-1.5 flex flex-wrap justify-center items-center gap-1.5 text-gold text-[13px] font-bold hover:opacity-80 transition-opacity">
                                <tMode.Icon className="w-3.5 h-3.5" />
                                <span>{t.linkedName}</span>
                                {t.durationMin > 0 && <span className="text-gold/60 font-normal">· {fmtDur(t.durationMin)}</span>}
                                <ChevronDown className={`w-3 h-3 transition-transform ${expandedLeg === legKey ? 'rotate-180' : ''}`} />
                              </button>
                            ) : (
                              <button onClick={() => setEditingTransit(s.id)}
                                className="py-1.5 flex flex-wrap justify-center items-center gap-1.5 text-gold text-[13px] font-bold hover:opacity-80 transition-opacity">
                                <tMode.Icon className="w-3.5 h-3.5" />
                                <span>{`${fmtDur(t.durationMin)} ${tMode.label.toLowerCase()}`}</span>
                                <Pencil className="w-2.5 h-2.5 opacity-60" />
                              </button>
                            )}
                            {t.linkedName && (
                              <span className="inline-flex items-center text-[9px] font-bold uppercase tracking-wide text-gold bg-gold/10 border border-gold/30 px-2 py-0.5 rounded-full">
                                From Logistics
                              </span>
                            )}
                            {t.linkedName && expandedLeg === legKey && (
                              <div className="w-full flex flex-col items-center gap-2 pt-1.5">
                                {t.linkedId && onExitToItinerary && (
                                  <button onClick={() => onExitToItinerary(t.linkedId)}
                                    className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-gold hover:opacity-80 transition-opacity">
                                    <LinkIcon className="w-3 h-3" /> View saved item
                                  </button>
                                )}
                                <button aria-label="Edit" onClick={() => setEditingTransit(s.id)}
                                  className="flex items-center gap-1 text-[11px] font-bold text-app-sub/60 hover:text-gold transition-colors">
                                  <Pencil className="w-2.5 h-2.5" /> Edit duration
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <button
                            onClick={() => itinTransports.length > 0 ? setAddingTransit(s.id) : setEditingTransit(s.id)}
                            className="pt-5 pb-1.5 flex items-center gap-1 text-gold/60 text-[10px] font-black uppercase tracking-widest hover:text-gold transition-colors">
                            <Plus className="w-3 h-3" /> Add transport
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </motion.div>
              );
            })}

            {/* ── Closing divider: the day's end time, with add + edit like the
                 per-slot dividers. Its "+" replaces the old standalone Add-spot. */}
            {plannedSlots(d.slots).length > 0 && (() => {
              const ordered = reSort(d.slots || []);
              const last = ordered[ordered.length - 1];
              const endMin = last ? toMin(last.start) + (last.durationMin || 0) : 0;
              // An end at (or past) midnight should read 12:00 AM, not the clamped 11:59 PM.
              const endLabel = endMin >= 24 * 60 ? '12:00 AM' : stampAt(endMin);
              return (
                <div className="flex items-center gap-2 py-2">
                  <span className="text-sm font-bold tabular-nums px-1.5 py-0.5 rounded-md text-app-sub/70 whitespace-nowrap">{endLabel}</span>
                  <div className="flex-1 h-px bg-app-border/30" />
                  <div className="flex items-center gap-1">
                    <button aria-label="Add a spot" onClick={() => openAdd(snap5(toHHMM(endMin)))} title="Add a spot"
                      className="w-9 h-9 flex items-center justify-center rounded-xl text-app-sub/50 hover:text-gold hover:bg-app-faint active:scale-95 transition">
                      <Plus className="w-[18px] h-[18px]" />
                    </button>
                    <button aria-label="Edit" onClick={() => openSlotEdit(di, last.id)} title="Edit the last spot"
                      className="w-9 h-9 flex items-center justify-center rounded-xl text-app-sub/50 hover:text-gold hover:bg-app-faint active:scale-95 transition">
                      <Pencil className="w-[17px] h-[17px]" />
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* ── Empty day: single add-spot CTA ── */}
            {/* Grey, not gold — matches the "Add to shortlist" empty-state
                treatment elsewhere; a gold dashed CTA read as more of a
                primary action than an always-there add button warrants.
                Dropped the earlier -mx-3 bleed too: widening just this one
                box past its own siblings' width read as misaligned once
                the strip/header above it became one consistent-width
                shape — better to match than to stand out.

                Gated on plannedSlots, not on d.slots: the first and last day
                of a destination always carries an arrival or departure leg,
                so "has slots" was true there and both this CTA and the
                closing divider's "+" went missing on exactly the two days of
                each destination that have nothing planned on them yet. A
                journey leg is not a plan. */}
            {plannedSlots(d.slots).length === 0 && (
              <button onClick={() => openAdd()}
                className="w-full py-5 rounded-2xl border-2 border-dashed border-app-border/50 bg-app-faint/30 text-app-sub/60 transition font-black text-xs uppercase tracking-widest active:scale-[0.98] hover:border-app-sub/40 hover:text-app-sub">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-app-sub text-white mb-1 shadow-lg shadow-app-sub/20">
                    <Plus className="w-6 h-6 stroke-[3]" />
                  </div>
                  Add spot
                </div>
              </button>
            )}

          </div>
      ))}

      {addSpotModal}

      {confirmRefresh && (
        <SpotModal title={confirmRefresh.title} onClose={() => setConfirmRefresh(null)}>
          <div className="space-y-4">
            <p className="text-sm font-medium text-app-sub leading-relaxed">{confirmRefresh.message}</p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmRefresh(null)}
                className="flex-1 py-3 rounded-xl border border-app-border text-app-sub text-[11px] font-black uppercase tracking-widest hover:bg-app-faint transition">
                Cancel
              </button>
              <button onClick={() => { setConfirmRefresh(null); onAutoFillTrip(); }}
                className="flex-1 py-3 rounded-xl bg-gold text-white text-[11px] font-black uppercase tracking-widest hover:brightness-110 transition">
                Refresh Plan
              </button>
            </div>
          </div>
        </SpotModal>
      )}

      {showPending && (
        <SpotModal title="Pending Shortlist Items" onClose={() => setShowPending(false)}>
          <div className="space-y-4">
            {unplaced.length === 0 ? (
              <p className="text-sm font-medium text-app-sub/60 text-center py-6">Nothing pending — every committed item already has a spot.</p>
            ) : (
              <div className="space-y-2">
                {/* Card layout mirrors the Shortlist tab's own cards (avatar +
                    icon/name/category), with the same green "Committed to
                    Plan" calendar toggle as the deselect action. */}
                {unplaced.map(item => {
                  const l1Key = item.type === 'attraction' ? 'experiences' : item.type === 'eat' ? 'food' : item.type === 'shopping' ? 'shopping' : 'nightlife';
                  // Shopping has no subcats (no per-item category), so
                  // subcatEmoji would fall back to a generic pin — use the
                  // same fixed bag emoji as everywhere else instead.
                  return (
                    <div key={item.id} className="bg-white border border-app-border rounded-2xl p-3 pb-4 shadow-sm space-y-2">
                      <div className="flex justify-between items-center">
                        <By userId={item.addedBy} />
                        <button aria-label="Remove from plan"
                          onClick={() => onUncommitPending(item)}
                          title="Remove from plan"
                          className="h-7 w-7 rounded-full flex items-center justify-center bg-emerald-500 text-white"
                        >
                          <Calendar className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <div className="w-9 h-9 rounded-xl bg-app-faint flex items-center justify-center text-lg flex-shrink-0">
                          <SubcategoryIcon name={item.category} l1={l1Key} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-app-text leading-snug line-clamp-1">{item.name}</div>
                          <div className="ds-label mt-0.5">{normalizeSubcat(item.category)}</div>
                        </div>
                      </div>
                      {item.notes && <p className="text-xs text-app-sub leading-relaxed line-clamp-2">{item.notes}</p>}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => setShowPending(false)}
                className="flex-1 py-3 rounded-xl border border-app-border text-app-sub text-[11px] font-black uppercase tracking-widest hover:bg-app-faint transition">
                Close
              </button>
              <button
                disabled={unplaced.length === 0}
                onClick={() => { setShowPending(false); onAutoFillTrip(); }}
                className="flex-1 py-3 rounded-xl bg-gold text-white text-[11px] font-black uppercase tracking-widest hover:brightness-110 transition disabled:opacity-40 disabled:pointer-events-none">
                Refresh Plan
              </button>
            </div>
          </div>
        </SpotModal>
      )}

      {directionsFor && (
        <SpotModal title="Open directions" onClose={() => setDirectionsFor(null)}>
          <div className="space-y-2">
            <a href={googleMapsDirUrl(directionsFor.a, directionsFor.b, directionsFor.mode)}
              target="_blank" rel="noreferrer" onClick={() => setDirectionsFor(null)}
              className="w-full py-3.5 rounded-xl border border-app-border text-app-sub text-[11px] font-black uppercase tracking-widest hover:bg-app-faint hover:text-gold transition flex items-center justify-center gap-2">
              <MapIcon className="w-4 h-4" /> Google Maps
            </a>
            {isIOS && (
              <a href={appleMapsDirUrl(directionsFor.a, directionsFor.b, directionsFor.mode)}
                onClick={() => setDirectionsFor(null)}
                className="w-full py-3.5 rounded-xl border border-app-border text-app-sub text-[11px] font-black uppercase tracking-widest hover:bg-app-faint hover:text-gold transition flex items-center justify-center gap-2">
                <MapIcon className="w-4 h-4" /> Apple Maps
              </a>
            )}
          </div>
        </SpotModal>
      )}

      {/* ── Undo snackbar (delete / optimise) ── */}
      <AnimatePresence>
        {undo && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }}
            className="sticky bottom-3 z-40 mx-auto w-fit flex items-center gap-3 bg-app-text text-white rounded-full pl-4 pr-1.5 py-1.5 shadow-xl">
            <span className="text-[11px] font-bold">{undo.label}</span>
            <button onClick={doUndo} className="flex items-center gap-1 bg-white/15 hover:bg-white/25 rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-widest transition-colors">
              <Undo2 className="w-3 h-3" /> Undo
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
